// vuex store for portal application
// Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams

import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
	state: {
		// this imports the version number from package.json, so we can show the version number in the ui with $state.store.app_version; also need something in vue.config.js (PACKAGE_VERSION)
		// run the following to update the third number; use 'minor' to update the second number and 'major' to update the first number
		// npm --no-git-tag-version version patch; npm run build; npm run serve
		app_version: process.env.PACKAGE_VERSION || '0.0.0',

		// generally site_config values to what's needed for GA Inspire
		site_config: {
			// if this is set we'll show a maintenance message and force to the signin screen unless the user uses the magic password
			'down_until': '',

			'app_name': 'Cureum',
			'agency_name': 'Georgia Department of Education',
			// 'agency_name_css': 'top:-7px!important;line-height:35px!important;',

			// each instance should store a unique `cureum_code` -- used
			// a) when we want to regulate who can edit activities created from the cureum (e.g. Sparkl Commons)
			// b) for using the Sparkl add-on tool to launch cureum assets
			// if a site_config.cureum_code value isn't found, we'll use site_config.app_name instead (see getter below, and get_sparkl_lti_launch.php)
			// 'cureum_code': 'SPCOM',
		
			// included in index.html as the browser window/tab title
			'index_title': 'Cureum',
		
			// for most instances, add to /src/logos/; GA inspire is special...
			'agency_logo': 'https://inspire.gadoe.org/src/logos/GaDOE Logo RGB 600px wide.png',
		
			// banner color and color of the "agency" string
			'banner_color': '#F4DDCB',
			'agency_color': '#CB6015',
			'app_color': '#FFFFFF',
			'agency_name_font_family': "'Port Lligat Slab', 'Roboto', 'Calibri', 'Helvetica Neue', 'Helvetica' , 'Arial', sans-serif",
			'agency_name_font_family_href': 'https://fonts.googleapis.com/css2?family=Port+Lligat+Slab&display=swap',
			'banner_user_name_color': '#222222',

			// vue primary/secondary colors (used, e.g., for buttons)
			'vue_primary_color': '#206166',
			'vue_secondary_color': '#ef6400',

			// color used in messages interface
			'vue_messages_color': '#cccccc',

			// spinner colors; if not provided, we'll use vue primary and secondary colors
			'spinner_color_1': '',
			'spinner_color_2': '',

			// colors for asset types
			// This can be overridden for individual (or multiple/all) properties in config.php, e.g.:
			// 'asset_type_color' => [ 'assessment' => '#ffffff' ]
			// supported properties are 'lesson', 'sparkl_activity', 'sparkl_activity_bank', 'assessment', 'leader_resource', 'teacher_resource', and 'student_resource'
			/** JR Adding defaults in to prevent having to hard code them (eg. ResourceCollectionItem lines 576-603) */
			'asset_type_color': {lesson: '#2962ff', unit_plan:'#827717', sparkl_activity: '#651fff', sparkl_activity_bank: "#D50000", assessment: "#c51162", leader_resource: "#4e342e", teacher_resource: "#00695c", student_resource: "#e65100"},

			// background images
			// In most cases, add to /src/bgd-imgs; For GA inspire we will use the images in (/vue-cli/public)/bgd-imgs
			'site_bgd_images': [
				'/bgd-imgs/stock-1.1.jpg',
				'/bgd-imgs/stock-2.1.jpg',
				'/bgd-imgs/stock-3.jpg',
				'/bgd-imgs/stock-4.jpg',
				'/bgd-imgs/stock-5.jpg',
				'/bgd-imgs/stock-6.jpg',
				'/bgd-imgs/stock-7.jpg',
			],

			// css/text to adjust the way the agency logo and app title appears in the banner
			'agency_img_css': 'height:64px; top:4px; left:0; background-color:#f4ddcb; padding:4px 16px 8px 12px;',
			'banner_logo_css': 'font-weight:bold; left:120px; top:-3px; color: #e20177; background: -webkit-linear-gradient(#ef6400, #e20177); -webkit-background-clip: text; -webkit-text-fill-color: transparent;',
			'banner_logo_text': 'Inspire',
			'banner_subdomain_css': 'left:300px;',
			// note that the below won't be used for oidc login
			'login_wrapper_css': '',
			'login_logo_img_css': '',
			'login_logo_text_css': 'font-weight:bold;',
			'login_logo_text': 'Inspire',
			'main_background_screen_css': '',	// this can overwrite the 'screen' on background images

			// show a custom message on the login form
			'login_msg': '',
			
			// satchel name/origin
			'satchel_app_name': 'SuitCASE',
			'satchel_origin': 'https://case.georgiastandards.org',
		
			// sparkl name/origin
			'sparkl_app_name': 'Velocity', 
			'sparkl_origin': 'https://velocity.gadoe.org',

			// sparkl_embedder_origin -- used by sparkl when embedded to do some behavior switches
			'sparkl_embedder_origin': 'inspire',
		
			// Used in tooltip in CourseView: "Georgia Learning Standards for Science"
			'learning_standards_prefix': 'Georgia',
		
			// Used in tooltip in CourseView: "GaDOE Communities for Science"
			'communities_prefix': 'GaDOE',
		
			// Used in tooltip and as button text in CourseView:
			'pd_hub_prefix': 'Georgia Learns',
		
			'copyright_text': '© 2024 Georgia Department of Education',

			// if true, show link to TOS (which should be stored in config/home_page_content_terms_of_service.html) in MainView
			'show_terms_of_service': false,

			// if true, the resource usage report (AdminResourceUsage) reads from cubby instead of resource_usage_report
			'cubby_enabled': false,
			// base url of the cubby api (e.g. http://localhost:3000); used when cubby_enabled is true
			'cubby_url': '',

			// if true, the Lesson Plan Companion asks the AI API directly (with a token from get_ai_token) instead of openai_query;
			// turn on only after this instance is set up as a site in the AI API (infrastructure/hosts/services-01/sites/ in the monorepo)
			'ai_service_enabled': false,

			// if 'yes', require the user to read and agree to the TOS the first time any user starts using the site
			'require_agree_to_terms_of_service': 'no',
		
			// set to 'yes' to hide the login buttons, unless you go directly to /login
			'backdoor_login_only': 'no',
			
			// login method -- cureum or oidc (oidc currently used only for GADOE)
			'login_method': 'oidc',	// 'cureum', 'oidc'

			// note that most other options around login below won'e be used if login_method is 'oidc'

			// allow for a login with username and password; optional message to show above this option
			'allow_password_login': 'yes',
			'password_login_message': '',

			// allow google login; optional message to show above this option
			'allow_google_login': 'no',
			'google_login_message': '',
			// enable the google "one-tap" login (only has any effect if 'allow_google_login' is set to 'yes')
			'allow_google_login_one_tap': 'yes',
			// ** if 'yes', create new accounts automatically if needed when a user signs in with google for the first time
			'google_login_auto_provision': 'no',

			// allow msft login; optional message to show above this option
			'allow_msft_login': 'no',
			'msft_login_message': '',
			
			// ** show button for rapid identity oidc login (skyline)
			'allow_oidc_ri_login': 'no',

			// order in which to list the options above if multiple are showing. note that to be included, an option must both be in this list, AND the 'allow_xxx_login' option must be 'yes'
			'login_option_order': ['password', 'google', 'msft'],

			// show the "create account" button on the signin page
			'show_create_account_on_login': 'yes',

			// show the "forgot password" button on the signin page
			'show_forgot_password_btn': 'no',

			// external file storage system to use: currently support 'none' or 'equella'
			'file_storage_system': 'equella',

			// set to 'yes' to enable user creation from the AdminUsers component
			'enable_new_user_creation_from_admin_users': 'no',

			// set to 'yes' to enable the "Simulate another role..." menu item for "normal" instances or "sandbox" instances (note that we shouldn't have both on at the same time)
			'enable_simulate_role_menu': 'yes',
			'enable_sandbox_simulate_role_menu': 'no',

			// whether or not to show signin/create account buttons in the banner
			'show_banner_signin_btn': 'no',
			'show_banner_create_account_btn': 'no',
			'banner_signin_btn_color': 'primary',

			// whether or not to show user system data on the home page
			'show_user_system_data_on_home': 'yes',

			// email domains that get "Import to district" menu options (if empty, no one will get these options)
			'import_to_district_domains': {},	// {'henry.k12.ga.us': 'HenryConnects'},	// , 'commongoodlt.com'
			// the prefix that will be added to lesson/resource codes when exporting from this cureum for import to another cureum
			'import_from_cureum_prefix': '',	// e.g. 'INSPR'
			// mappings of `import_from_cureum_prefix` prefix codes to cureum domains for import to this cureum
			'import_prefix_code_mappings': {
				'INSPR': 'https://inspire.gadoe.org',
			},

			// whether or not to show 'communities' and 'pd hub' links (this will probably need to be made more general later, to support different buttons in different instances)
			'show_extra_subject_links_in_course_index': 'yes',

			// Added 11/20/2023 for Sparkl: tabs to include; default is 'all', or specify array with values from ['home', 'classes', 'resourcerepos', 'pd', 'mycollections', 'standards'] (see WelcomeView)
			'main_tabs_to_include': ['home', 'classes', 'resourcerepos', 'mycollections', 'standards'],

			// this can be used to include a site header for the popup page (it's called *home*_header_html for historical reasons)
			'home_header_html': '',

			// this can be used to include a site footer
			'site_footer_html': '',

			'summer_2026_css': 'no',

			// Allows registering custom SVG icons at runtime so they can be used anywhere a Vuetify icon is accepted.
			// Shape: { icon_id: '<svg>...</svg>', ... }
			//   icon_id   — the identifier you'll reference in templates (no '$' prefix here; Vuetify adds it automatically)
			//   svg value — the full <svg>...</svg> string for the icon
			// Example implementation in config.php:
				// 'custom_icons' => [
				// 	'my_custom_icon' => '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="..."/></svg>',
				// ],
			// Usage in templates: <k-icon>$my_custom_icon</k-icon>
			'custom_icons': {},

			// SF: added 9/13/2024 for ALEX.
			// Allows for optional custom title and icon in navigation tabs
			// Example implementation in config.php :
				// 'customized_tabs' => [
				// 	'classes' => [
				// 		'icon' => 'fa-sharp fa-solid fa-person-chalkboard',
				// 		'title' => 'Content Area',
				//		'description_style' => 'line-height:12px',
				//		'description' => 'This is the tab description',
				// 	],
				// 	'resourcerepos' => [
				// 		'icon' => 'fa-sharp fa-regular fa-book-bookmark',
				// 	],
				// ],
			'customized_tabs': {
				'home': {},
				'classes': {},
				'resourcerepos': {},
				'pd': {},
				'mycollections': {},
				'standards': {},				
				'admin': {},
			},

			// SF: added 10/21/2024 for ALEX
			// Allows for customized tab captions in the Unit Mode Toggle (see CollectionUnit.vue for implementation)
			// Example implementation in config.php :
				// 'unit_mode_toggle_option' => [
				// 	'resources' => 'Featured Content'
				// ],
			'unit_mode_toggle_option': [],
			'unit_descriptions_collapsible': 'yes',

			// if this is true, we'll show a pop-up when the user first loads, showing either special pop_up content (if pop_up is included in home_page_variants) or the home page content
			'show_pop_up': false,

			// configurations for the pop up
			'pop_up': {
				'icon': 'fas fa-up-right',
				'header_icon': 'fas fa-up-right',
				'close_button': {
					'icon': false,
					'text': "Let's get started!"
				}
			},

			// route to start with when the user first launches (if no route is specified in the initial launch)
			'app_starting_route': '',
			// 'app_starting_route': '/courses',

			// configurations for the support icon
			'support_icon': {
				enabled: false,
				icon: 'fas fa-envelope',
				tooltip: '',
				title: 'Support'
			},

			// configurations for the user guide
			'user_guide': {
				'enabled': false,
				'icon': 'fas fa-circle-info',
				'color': 'light-blue accent-2',
				'tooltip': 'Open User Guide',
				'title': 'User Guide',
				'satchel_domain': 'https://hd.satchelcommons.com',
				'guide_framework_id': 'GUID',
				'help_flavor': '',
			},

			// "schemes" for collections and units (see learning_progressions.js)
			'collection_plan_schemes': [],
			'unit_plan_schemes': [],
			'course_plans_enabled': 'no',
			'unit_plans_enabled': 'no',

			// which home page variants to support
			'home_page_variants': ['staff', 'student', 'parent', 'unsignedin'],

			// which content types to support; e.g. could take out 'lessons' if the instance isn't supporting lessons
			'supported_content_types': ['lessons', 'activities', 'resources'],

			// if this is 'yes', when a user clicks to align a lesson or resource to standards, only 'outcomes' in CASE frameworks will be shown as options for alignments. See Satchel for the algorithm for what counts as an 'outcome'
			'align_assets_to_outcomes_only': 'no',

			// if yes, user can't do anything without signing in -- we show the signin interface as soon as the app launches if no session is active
			'require_sign_in': 'no',

			// if yes, require users to sign in to access "reslink" paths (as of 10/2025, 'yes' only for Henry)
			'require_sign_in_for_reslink': 'no',

			// if yes, when users view "reslink" paths, we will first show the resource "quickview"; if no, we will jump right to the resource's url or LTI launch (as of 10/2025, 'no' only for Henry)
			'show_quick_look_for_reslink': 'yes',

			// if this is yes, show the signin interface right away when a user first loads the site, but they can choose "guest mode" that allows them to view without signing in (but search below for exception)
			'show_signin_on_load': 'no',

			// if yes, we show *all* items and lessons when not signed in; if no, we limit to student-facing resources when not signed in
			'show_all_items_when_not_signed_in': 'yes',

			// whether or not to show the 'my' mycollections when not signed in -- might be, e.g., 'yes' for state implementations and 'no' for districts
			'show_mycollections_when_not_signed_in': 'yes',

			// whether or not to offer to show the 'Parent' role to all staff members
			'show_parent_role_to_all_staff': 'no',

			// whether or not this cureum instance is designed to support a designated student role
			'student_role_allowed': true,

			// noun to use for "Resource Repositories"
			'resourcerepos_noun': 'Resource Repositories',

			// label for the user's "my default content collection"
			'default_my_collection_label': 'My Content Sandbox',	// 'My Default Content Collection'

			// 7/23/2026
			// velocity-logo-full-color.png
			'default_my_collection_sparkl_unit_description': '<div class="d-flex align-center justify-center"><img src="https://sparkl-ed.com/images/sparkl-logo-fff-200.png" style="float:left; margin-right:12px; height:48px"><div style="font-size:18px;text-align:center;">Create and manage your %sparkl_app_name% Activities here.<div class="mt-2">To learn more, see the %sparkl_app_name% <a href="https://satchelcommons.com/a0cdefbe-f528-4bea-b4f2-020e5b650049/overview?embedded" target="_blank"><b>User Guide</b></a>.</div></div></div>',

			// whether or not to include the "Include PDF resources" option when printing lessons
			'include_pdf_for_lesson_print': 'yes',

			// whether or not to enable the "AI enhanced" lesson option for NEW LESSONS
			// note that regardless of this setting, we'll show/edit the lesson using the enhanced editor if lp_variant is 'B' and using the "old" editor if lp_variant is 'A'
			// 'no': never allow users to create using the LLM editor
			// 'always': users always create new lessons using the LLM
			// 'optional': users have the option of using the LLM or "traditional" lesson editor for new lessons (old value: 'yes')
			'enable_llm_lesson_plans': 'no',

			// if this is 'yes', we will allow lessons to be converted back and forth between variants A and B
			// if this is 'always', we will ALWAYS convert A to B
			'allow_llm_lesson_conversion': 'no',

			// if this array includes course_codes and enable_llm_lesson_plans is 'optional', the enhanced editor will only be enabled for new lessons in the specified collection(s)
			'alba_collections': [],

			// if this is 'no', we won't support "OG" lessons at all (only LLM editor)
			'support_og_lessons': 'yes',

			// these can be used to change things in the LPC; LessonEditorEnhanced has default values
			'llp_supp_info_header': '',
			'llp_warning_start': '',
			'llp_warning_finish': '',
			'lpc_muted_colors': 'no',	// added for CPS; see LessonEnhancedMixin

			// if specified this is a list of stop words that we don't want going to, or coming from, the AI
			'lpc_stop_words': [],

			// if lpc_show_author_note is 'yes', show an author not in Lesson Plans (see LessonView);
			'lpc_show_author_note': 'no',
			'lpc_agency_sanctioned_author_note': '',	// see LessonView

			// this can be used to completely rewrite the "variant B" ("lesson plan companion") master data
			'variant_b_lesson_master_data': null,
			// or this can be used to make edits to the default LPC lesson plan components (see abov)
			'variant_b_lesson_plan_edits': null,

			// 7/23/2026: default for collection_view_mode
			'default_collection_view_mode': 'tiles',	// list / tiles

			// if this is 'yes', we show the "quick look" dialog for all resources
			'quick_look_for_all_resources': 'no',

			// if this is 'yes', we allow users to filter resources by source
			'enable_resource_filtering_by_source': 'no',

			// if this is true, we will show the resource ratings options on resource "quick look" dialog
			'show_resource_ratings': false,

			// whether or not to enable "pinning" of resources
			'enable_resource_pinning': false,

			// 7/23/2026: if 'yes', when teachers copy links to sparkl activities, we will give them the "reslink" version, which goes through Cureum, rather than the direct-to-sparkl links
			'use_reslinks_for_sparkl_urls': 'no',

			// if this is set, we show this text in a tooltip on the "open in new tab" button in the resource quicklook
			'open_resource_tooltip_content': '',
			
			// whether or not to show the 'Submit Feedback' option in the resource tile menu
			'show_submit_feedback_option_for_resources': 'no',

			// whether or not to show the "Weekly Lesson Plans" option, currently appearing in the user menu
			'show_weekly_lesson_plans': 'no',

			// whether or not to show the "school leader tools" option, currently appearing in the user menu and in a button at the top of collections
			'show_school_leader_tools': 'no',

			// whether or not to show the 'mimic another user' option in the user menu for regular staff users; su users always see this option
			'staff_can_mimic_users': 'no',

			// ** whether or not to limit viewable agency-sanctioned courses by academic_year; defaults to 'no' (for GA Inspire); should be set to 'yes' for districts
			'limit_courses_to_academic_year': 'no',

			// if 'yes', allow superusers to edit content from anywhere; normally you have to be in a collection view
			'allow_superusers_to_edit_from_anywhere': 'no',

			// default values for new "horizontal" collections
			'default_new_collection_scope_and_sequence_label': 'Curriculum Map',
			'default_new_collection_arrange_by_terms': 'no',
			'default_new_collection_specify_unit_numbers': 'yes',
			'default_new_collection_specify_unit_intervals': 'no',

			// course grades and subjects to include, and repository course codes to include (this is designed to be used when we're setting up a new installation and want to start with copies from another installation)
			// NOTE: this is more efficiently taken care of by the agency_sanctioned flag, but leave these here in case they come in handy later
			'grades_to_include': null,
			'subjects_to_include': null,
			'repositories_to_include': null,

			// grades
			// note that if the list of grades changes, might need to update code in CourseIndex
			'grades': [ 'PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12' ],
			// note that for a time we defined subjects here, but this has now been moved to the config file so it can be managed with an admin tool

			// if set, create a default set of folders when a new unit is created in a course collection
			'default_folders_for_course_units': null,
			// 'default_folders_for_course_units' => [
			// 	'leader_resources' => ['title' => 'Leader Resources', 'color' => 'brown'],
			// 	'course_guidance' => ['title' => 'Resources for Course Guidance', 'color' => 'cyan', 'cross_unit_key' => 'rcg'],
			// 	'unit_planning' => ['title' => 'Unit Planning Documents', 'color' => 'green'],
			// 	'tcc' => ['title' => 'tcc items', 'color' => 'indigo'],
			// 	'assessments' => ['title' => 'Assessments', 'color' => 'pink'],
			// 	'templates' => ['title' => 'Template Lesson Plans and Student Activities', 'color' => 'blue'],
			// 	'supplementals' => ['title' => 'Supplemental Resources', 'color' => 'blue-grey', 'children' => [
			// 		'student_ese' => ['title' => 'Additional Student ESE Resources', 'color' => 'teal'],
			// 		'student_advanced' => ['title' => 'Additional Student Advanced Learning Resources', 'color' => 'purple'],
			// 		'additional_student' => ['title' => 'Additional Student Resources from Unit Planning Guides', 'color' => ''],
			// 		'additional_teacher' => ['title' => 'Additional Teacher Resources from Unit Planning Guides', 'color' => ''],
			// 	],
			// ]],

			// if this is set, it will indicate the lp_id and title for a collection that will receive items when they are "unpublished" (see ResourceCollectionItem)
			'unpublished_items_collection': null,

			// whether or not to enable functionality for...
			'show_pd': 'no',						// pd collections
			'use_term_mode': 'no',					// block/normal term mode distinction for course

			// deprecated as of 6/2026
			'manage_assignments': 'no',				// Assignment Center functionality (the ability to create Directives within Units)
			'use_message_center': 'no',				// Message Center functionality
			
			'show_course_alt_cureum_btn': 'no',		// link to an alternative cureum site for courses (e.g. Inspire from HenryConnects)
			'show_safari_integration': 'no',		// links to Safari LOR searches
			'show_scope_sequence': 'no',			// dynamically-created scope & sequence document for a course
			
			'enable_admin_term_management': 'no',	// allows admin to manage when each term (e.g. quarter) starts/stops
			'enable_admin_lti_management': 'yes',	// allows admin to manage LTI tools for sparkl
			'enable_subdomains': 'no',				// enables 'subdomain' functionality
			'subdomain_admin_title': 'Agencies / LTI Config',

			'show_update_sis_data_btn': 'no',		// for districts: allows user to force-update SIS data
			'hide_unit_dates_in_lps': 'no',		// showing explicit dates in horizontal learning progressions, *if* we have week dates specified; if this is 'no' we'll hide them
			'enable_add_to_gc_in_kebab': 'no',		// if 'yes', show the "Add to GC" option in the kebab menu for resources
			'use_google_classroom_addon': 'no',		// if enable_add_to_gc_in_kebab is 'yes' and this is 'yes', we will show the AddToGoogleClassroom component to add the selected item using the Sparkl GC Addon
			'hide_collection_button_strip': 'no',	// if 'yes', hide the strip of buttons at the top of collections
			'hide_unit_view_header': 'yes',	// if 'yes', hide the "k-unit-view-header" div in collection units
			'export_common_cartridge_enabled': 'yes',	// 7/23/2026
			'hide_toolbar_search': 'no',	// 8/20/2026
			'lazy_load_some_collections': 'no',		// 8/22/2026
			'hide_unaligned_items_in_search': 'no',		// 9/1/2026 -- added for ALEX

			'include_assessment_resources': 'no',	//whether or not to include "assessment" type resources
			'include_block_or_traditional_setting_for_resources': 'no',	//whether or not to include the "block/traditional" setting for resources
			'include_family_setting_for_resources': 'no',	// whether or not to include the "Show to family members" setting for resources
			'show_restricted_resources_control': 'no',	// whether or not to include the "authorized teachers only" setting for resources
			'resource_content_type_options': [ 'Learning Activity', 'Assessment', 'Audio/Video', 'Interactive/Game', 'Informational Material', 'Lesson/Unit Plan' ],	// list of possible "Content Type" options for resources (see ResourceEditor)

			// whether or not to show the "Target Students" and "Special Resource Category" menus in the resource editor
			'show_target_students_menu_in_resource_editor': 'no',
			'show_special_resource_category_menu_in_resource_editor': 'no',

			'enable_flex_units': 'no',	// whether or not to show the option to create a flex unit

			// if this is 'yes', assets that don't have folder_assignments in a unit will be added to the 'top' folder for the unit
			// we *don't* want this for some legacy situations (e.g. Inspire and Henry); but we may need to do it in some accident-recovery situations
			'add_unlinked_assets_to_unit_top': 'no',

			// button text for course standards on the course page
			'course_standards_btn_text': 'Course Standards',

			// button text for "alt cureum" button on the course page; this will open site_config.external_cureum_origin, which must be also be set
			// also note that the alt cureum button will only be shown if show_course_alt_cureum_btn is set to 'yes'
			'course_alt_cureum_btn_text': 'GaDOE Inspire',
			'external_cureum_origin': 'https://inspire.gadoe.org',

			// if the time zone for this cureum's server isn't GMT, use this to adjust when we make calls to convert_to_local_date
			// e.g., if the server's TZ is EST, we would use 300 (5 hours difference)
			// leave this UNSET by default though; see U.convert_to_local_date
			// 'convert_to_local_date_offset_adjustment': 0,

			// ** whether or not we're using oneroster to connect with a SIS
			'use_sis_connection': 'no',

			// ** 'yes' if we want to record/display separate usage stats for staff/students/parents
			'record_stats_by_role': 'no',

			// These are the material UI colors from previous options
			'swatches': [
				['#C62828'],
				['#AD1457'],
				['#6A1B9A'],
				['#4527A0'],
				['#283593'],
				['#1565C0'],
				['#0277BD'],
				['#00818D'],
				['#00695C'],
				['#2E7D32'],
				['#558B2F'],
				['#9E9D24'],
				['#F9A825'],
				['#FF8F00'],
				['#EF6C00'],
				['#BF360C']
			],

			// this allows us to specify additional CASE frameworks to offer as options for the "Home" Standards Framework in the collection editor
			'case_framework_options_for_collections': {
				// '67556983-bfbc-4a99-a532-e248874a8595': 'Danielson Group’s Framework for Teaching (FFT)',
				// '535625ee-9b10-4022-b27d-c935fcf2ba69': 'Georgia Early Learning and Development Standards'
			},

			// if this is 'yes', then in the "Standards tab" tab of a course LP unit, we show the resource list by standard right away; if 'no', the user initiates whether or not to show standards-related resources
			'immediately_show_standards_aligned_resources': 'no',

			// if this is 'yes', show in CollectionUnit a count of all assets aligned to the unit (ALEX, where the header for this tab is "Content By Standard") -- otherwise we show a count of the # of standards
			'show_resource_count_in_unit_standards_tag': 'no',

			// if this is 'yes', then we open all folders by default (unless the user explicitly chooses to close them) for course collections
			'open_folders_by_default_for_course_collections': 'no',

			// ** limit_search_to_aligned_resources: currently only used on back-end, but listed here with default value ('no') for completeness
			'limit_search_to_aligned_resources': 'no',

			// messages with warnings/disclaimers for various things...
			// shown when viewing the "import code" for lessons/sparkls/resources
			'share_lesson_warning': '',
			'share_sparkl_warning': '',
			'share_resource_warning': '',
			// shown when viewing the import buttons from ResourceSearch
			'import_warning': '',
			// shown at the bottom of the "My Collections" index page
			'my_collections_warning': '',
			// shown when you copy a lesson for your own use
			'copied_for_my_use_warning': '',
			// shown at the bottom of resources/lessons that aren't agency sanctioned
			'not_agency_sanctioned_warning_resource': '',
			'not_agency_sanctioned_warning_lesson': '',

			// enable asset manager
			'asset_manager_enabled': false,

			// 7/23/2026: if true, only allow collection editors to access the HQIM interface for resources
			'asset_manager_enabled_for_collection_editors_only': false,

			// enable copying a collection to a subdomain (Arizona subdomain pilot)
			'enable_subdomain_collection_copy': false,

			// require standards alignment to submit asset for publication
			'asset_submission_requires_alignment': true,

			// require initial review to submit asset for publication
			'asset_submission_requires_review': true,

			// enable payment tracking
			'payment_tracking_enabled': false,

			// asset review notifications; admin_emails receive submitted/approved events
			'asset_notifications': {
				'enabled': false,
				'admin_emails': [],
			},
			
            'asset_manager': {
				enabled: false,
				text: {
					name: 'HQIM Publishing Dashboard',
					name_simple: 'HQIM',
					status: 'HQIM Publishing Status',
					review_header: 'HQIM Review',
					review_rubric: 'HQIM Rubric',
					review_required: "'HQIM Review'",
					email_subject: 'Check Your ALEX HQIM Dashboard',
					email_body_text: '<html><head><title>Account support</title><head><body><p>There are updates available on your ALEX HQIM Dashboard. Please log in to your ALEX account to review the updates at <a href="https://alabamalearningexchange.org">https://alabamalearningexchange.org</a>.</p></body></html>',
				},
				collection_editors_only: false,
				submission_requires_alignment: true,
				submission_requires_review: true,
				payment_tracking_enabled: false,
				// if true, automatically create a comment when an active reviewer checks/unchecks a criteria item
				review_auto_comment_on_criteria_change: false,
				notifications: {
					enabled: false,
					admin_emails: []
				}
			},
			
			// always show collection unit resource tab (Henry request)
			'always_show_collection_unit_resource_tab': false,

			// heyc connection (Henry); heyc_origin must also be defined by the server, and these can also be defined: heyc_tenant_id / heyc_context_id / heyc_resource_menu_text / heyc_collection_btn_text
			'enable_heyc_connection': false,

			// google gemini connection (Henry); gemini_origin must also be defined by the server
			'enable_gemini_connection': false,

			// 2026/05/26 switch to use new "VX" navigation
			'use_vx_nav': false,
			// tabs to include; default is 'all', or specify array with values from ['home', 'favorites', 'classes', 'resourcerepos', 'pd', 'mycollections', 'standards'] (see MainViewVX)
			'main_tabs_to_include_vx': ['home', 'favorites', 'classes', 'resourcerepos', 'mycollections', 'standards'],

			// banner logo/html/text for home page
			'vx_home_banner_html': '',
			'vx_banner_logo': '',
			'vx_banner_logo_text': '',
			'vx_banner_logo_img_css': '',

			// if 'yes', we will show the signed-in user's favorite collection tiles on the home page
			'show_favorites_on_home': 'no',

			// PL (Professional Learning) event links shown alongside subjects in the course index.
			// Enable per-instance in config.php by setting 'enabled' => true and adding subject keys
			// that map to event URLs, where each key must match a subject name used in the course index:
			//   'pl_event_links' => [
			//     'enabled' => true,
			//     'Mathematics' => 'https://example.com/events/topic/mathematics',
			//     'Science'     => 'https://example.com/events/topic/science',
			//   ]
			'pl_event_links': {
				'enabled': false,
			},

			// asset review template definitions
			// include `$asset_type` to display `Lesson`, `Resource`, or `Activity` as appropriate for the asset
			// when doing so in php, make sure the string is bounded by ' not "
			// omit `scale` property to use a simple binary "Approved"/"Needs Improvement" score
			// if checking for standards, ensure the `id` for that section is `standards_alignment` and the system will check also
			'asset_review_templates': [
				{
					value: 'default',
					text: 'Default Rubric',
					definition: [
						{
							id: 'standards_alignment',
							title: 'Standards Alignment',
							description: '',
							required: true,
							asset_types: ['lesson', 'resource', 'activity'],
							scale: {
								0: '<b>No standards</b> specified',
								1: 'Primary standard(s) specified, but content of lesson/activity <b>does not target the full depth</b> of the primary standard(s)',
								2: '$asset_type <b>targets the full depth</b> of the primary standard(s)',
							},
						},{
							id: 'student_engagement',
							title: 'Student Interactivity/Engagement',
							description: '',
							required: true,
							asset_types: ['lesson', 'resource', 'activity'],
							scale: {
								0: '$asset_type includes <b>passive</b> student content only',
								1: '$asset_type includes <b>minimal student interactivity</b>',
								2: '$asset_type includes <b>engaging</b> student interactivity, <b>appropriate</b> for learning about the standard',
								3: '$asset_type includes <b>exceptionally engaging and appropriate</b> student interactivity',
							},
							criteria: [
								{
									id: 'student_engagement_a',
									text: 'Includes student <b>collaboration</b> component',
									control: 'checkbox'
								},
								{
									id: 'student_engagement_b',
									text: 'Includes <b>online</b> interaction with <b>teacher-accessible student results</b>',
									control: 'checkbox'
								},
							]
						},{
							id: 'assessment',
							title: 'Assessment',
							description: '',
							required: true,
							asset_types: ['lesson', 'resource', 'activity'],
							scale: {
								0: '<b>No assessment guidance</b> provided',
								1: '<b>Minimal</b> assessment guidance provided',
								2: '<b>Adequate</b> assessment guidance provided',
							},
							criteria: [
								{
									id: 'assessment_a',
									text: 'Includes <b>multiple-choice</b> questions',
									control: 'checkbox'
								},
								{
									id: 'assessment_b',
									text: 'Includes <b>free response</b> questions',
									control: 'checkbox'
								},
								{
									id: 'assessment_c',
									text: 'Includes <b>other question/interaction types</b>',
									control: 'checkbox'
								},
							],
						},{
							id: 'teaching_strategies',
							title: 'Teaching Strategies',
							description: '',
							required: true,
							asset_types: ['lesson'],
							scale: {
								0: '<b>No teaching strategies</b> provided',
								1: '<b>Some teaching strategies</b> specified, but not appropriately spelled out',
								2: 'Teaching strategies that are specified are appropriately spelled out, but <b>additional elements are needed</b>',
								3: '<b>Effective teaching strategies</b> specified and appropriately spelled out',
							},
							criteria: [
								{
									id: 'teaching_strategies_a',
									text: 'Questions: Gives students the opportunity to construct and/or provides engaging questions that spark and sustain inquiry.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_b',
									text: 'Collaboration: Structures opportunities for students to work together using disciplinary content to develop, examine, and/or communicate ideas.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_c',
									text: 'Academic vocabulary: Supports development of general academic and/or content area specific vocabulary in context.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_d',
									text: 'Analysis: Requires critical reading of primary and/or secondary resources.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_e',
									text: 'Speaking: Facilitates rich and rigorous evidence-based discussions.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_f',
									text: 'Writing: Expects students to reference evidence to inform, explain, evaluate, or make an argument (note taking, summaries, short responses, formal essays, etc.).',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_g',
									text: 'Culturally responsive teaching practices: Relates content to students’ cultural context.',
									control: 'checkbox'
								},
								{
									id: 'teaching_strategies_h',
									text: 'Civic engagement: Provides opportunities to apply learning through informed action.',
									control: 'checkbox'
								},
							]
						},
					]
				}
			],

			/** New Indicator */
			/** enabled */
			/** default number of days */
			/** hold window: 
			 * if set, assets published during this window will have the 
			 * `new_until_date` set the default number of days after the end of the window 
			 * */
			'new_indicator': {
				enabled: false,
				default_days: 30,
				hold_window: null,
			},
		},

		// these are the admin tools available to subdomain admins
		tools_for_subdomain_admins: ['home_page'],	// ['lti', 'users', 'home_page'],
		subdomain_data: null,

		// these are used for the AdminUsers tables
		admin_all_users: null,
		admin_all_user_count: 0,
		admin_admin_users: null,

		// note that grades and subjects used to be loaded from a static file on the server that was passed in the initialize_app service; now it's in site_config
		grades: [],
		subjects: {},

		// school_districts will be loaded if needed
		school_districts: [],

		available_academic_years: [],
		default_academic_year: '',
		allow_academic_year_change_for_all_staff: false,

		// For PD Resources
		todo_user_group_divisions: {},
		todo_user_group_schools: {},
		todo_user_group_warning_issued: false,
		todo_report_group_showing: '',
		todo_report_group_data: {},

		login_msg: '',
		login_error: null,
		user_info: {},
		simulated_user_info: {},
		actual_user_info: {},

		home_page_content: {},

		resources: [],	// TODO: not used??
		all_resources: [],	// TODO: not used??
		last_viewed_resource_id: '',

		n_collections_to_show_in_banner: 7,	// used in CollectionBase and Collection
		loaded_classes: false,
		sis_classes: [],
		added_my_courses: [],
		removed_my_courses: [],
		all_students: {},

		my_lessons: [],
		my_resources: [],
		my_ca_mappings: [],
		
		lesson_reports: [],
		user_records: [],	// used for holding looked-up user records for lesson_report collaborators/reviewers/owners

		// Assignment center stuff...
		my_activities: [],
		my_coteachers: [],
		my_coteaching_courses: [],
		my_activities_by_course: {},
		my_activity_results: {},

		// Message center stuff...
		messages: [],
		message_load_timestamp: 0,	// used to determine which messages to retrieve

		now_date_obj: new Date(),
		now_date_string: '',
		old_threshold_date_timestamp: 0,
		old_threshold_date_string: '',

		single_item: false,		// this will be get set to true if we're in a view where only a single item is showing -- /activity or /lesson
		single_item_mode: '',	// set in MyContentView2

		// these are to be deleted...
		my_copied_lessons: [],
		my_copied_activities: [],
		my_shadow_units: [],
		// froala_wrapper_components: {},	// used for implementing the "add resource" button in the froala editor

		ignore_published_edit_warning: false,
		ignore_lpc_stop_warning: false,

		quick_align_previously_opened: false,

		// see ResourceSearch; currently only for debugging purposes
		show_search_match_scores: false,
		asset_search_vectors: {},
		asset_search_term_vectors: {},

		// for SparklEmbed
		pm_event_listener_created: false,
		sparkl_embed_components: {},
		current_sparkl_embed_component: null,
		
		case_frameworks: {},	// used by the CASETree component to "cache" all loaded frameworks
		case_tree_showing: false,

		all_courses: [],
		all_courses_loaded: false,
		last_lp_list: null,	// used to know whether to go back to (the course index page or the my classes page) from a lp page
		course_update_trigger: 0,

		// for adding to google classroom as an addon
		gc_courses: null,

		lesson_masters: [],
		default_lesson_master: null,
		alba_warning_issued: false,
		lpc_materials_warning_issued: false,
		lpc_advanced_settings_showing: false,
		daily_lesson_date: '',
		daily_lesson_current_tab: '',

		google_client_id: '',

		froala_key: '',

		// checkout timestamp edit lock for collections
		// sent from server when aquiring  an edit lock
		lp_edit_locked: {},

		// this keeps track of the learning progression (course_code) that's currently showing; used for the socket and other things, i.e. CourseStandards
		lp_showing: '0',

		// FOR SPARKLSALT
		framework_records: [],
		framework_image_src_base: 'https://satchel.commongoodlt.com/src/filestore/images/',
		frameworks_loading: false,
		frameworks_loaded: false,
		case_tree_viewer_width: null,
		case_tree_viewer_height: null,
		association_type_labels: {
			exactMatchOf: 'Exact Match Of',
			replacedBy: 'Replaced By',
			hasSkillLevel: 'Skill Level',
			isRelatedTo: 'Related To',
			isPartOf: 'Part Of',
			precedes: 'Precedes',
			exemplar: 'Exemplar Of',
			isPeerOf: 'Peer Of',
		},
		framework_asset_counts: {},
		// the following two are used to directly load a framework and/or identifier in the standards tab
		starting_framework_identifier: '',
		starting_item_identifier: '',

		default_beta_options: {
			// sparkl_student_activities: false,
			// resource_repositories: false,
			// new_course_index: false,
		},

		user_is_touching: false,	// initialized below

		// Term settings, loaded from config
		// and writeable by admins back to config via admin Term Management UI
		term_settings: {},

		// if this gets set to false, stop warning the user about deletions -- but only until they reload
		confirm_resource_removals: true,

		hdcp_career_vectors: null,

		// vapp.$store.commit('lst_set', ['beta_options', {sparkl_student_activities:true}])

		// "local_storage settings": set defaults here; lst_initialize is called on initialization; call lst_set to set new values, possibly in computed:
		// foo: {
		// 	get() { return this.$store.state.lst.foo },
		// 	set(val) { this.$store.commit('lst_set', ['foo', val]) }
		// },
		// @update:foo="(val)=>foo=val"
		lst: {
			simulated_date: '',
			simulated_role: '',
			simulated_user: '',

			oidc_return_path: '',

			// the following variables control which "classes" view is showing on the welcome page -- 'favorites' or 'lpindex'
			unsigned_index_view_flavor: '',				// this is for if you're not signed in, used for courses, repos, or my (though you don't actually have any my collections)
			course_index_view_flavor: 'favorites',		// this is for the courses tab
			my_index_view_flavor: 'lpindex',			// this is for the "my" tab
			repo_index_view_flavor: 'lpindex',			// this is for the repo/pd tab

			welcome_section_showing: '',	// which "tab" is showing on the home page
			last_app_launch_timestamp: 0,
			
			login_email: '',
			beta_options: {},
			courseindex_opened_category: null,
			courseindex_opened_subcats: {},
			collections_opened_folders: {},
			unit_mode: 'resources',
			froala_image_size: '500',
			froala_paste_mode: 'normal',
			froala_image_display: 'block',
			froala_image_align: 'center',
			// froala_image_style: '',
			froala_image_scaled_size: '',

			admin_lti_type_showing: 'all',

			mathlive_advanced_options: false,
			mathlive_chosen_keyboard_index: 1,

			local_added_my_courses: [],
			sparkl_demo_email: '',	// see SparklEmbed
			collection_view_mode: 'tiles',	// other value is 'list'; the default can be set with site_config.default_collection_view_mode
			unit_descriptions_collapsed: false,
			collection_descriptions_collapsed: false,
			default_collection_sort_by: 'title',	// other value is 'created_at'
			default_collection_sort_by_created_at_order: 'desc',	// other value is 'asc'
			resource_search_sort_by: 'title',	// other value is 'created_at'
			resource_search_sort_by_created_at_order: 'desc',	// other value is 'asc'
			resource_search_show_secondary_alignments: false,

			last_collections_viewed: [],
			search_crit: 'keywords',
			search_subject: '',
			search_item_type_showing: '1lesson',
			selected_resource_search_types: [0,1,2],
			selected_resource_filter: 'none',
			resource_selector_new_or_search: 'new',
			resources_source_filters: {},

			child_email_showing: '',

			resource_editor_resource_type: '',
			resource_editor_teacher_facing: true,
			resource_editor_content_source: '',
			resource_editor_content_type: '',
			resource_editor_license_uri: '',
			resource_editor_license_text: '',
			resource_editor_standards: [],
			resource_editor_additional_metadata_showing: false,
			resource_editor_case_framework_identifier: '',

			collection_editor_case_import_url: '',
			collection_editor_case_branch_identifier: '',

			todo_report_show_empty_users: false,

			default_instance_unit: {}, // We store the most recently selected instance unit for each flex unit, keyed to flex unit lp_unit_id

			collection_assignments_or_messages_mode: 'unit_assignments',	// unit_assignments, course_assignments, messages
			my_content_items_mode: 'list',	// gantt
			my_content_assigned_to_filter: {},
			show_older_items: false,
			term_for_assignments: 0,
			assignment_chooser_sections: {},	// see SectionChooser.vue
			message_chooser_sections: {},		// see SectionChooser.vue
			bulk_activity_import_data: {},
			bulk_activity_settings: {},

			lp_format_showing: {},	// one value for each course code

			use_llm_for_new_lessons: false,
			edit_llm_suggestion_prompts: false,
			gpt_model: 'gpt-4o-mini',
			gpt_temperature: '1',
			alba_settings: {},
			lpc_time_spec: '',
			lpc_group_showing: {},

			show_inactive_collections: true,

			current_subdomain: '',

			hdcp_selected_career_identifier: '',

			gc_courses_for_assignment: [],
			gc_description_for_assignment: '',
			gc_max_points_for_assignment: 100,
			gc_publish_immediately_for_assignment: true,
		},
		lst_prefix: 'inspire_local_storage_setting_',
	},
	getters: {
		// allow beta_options to be specified in mapGetters
		beta_options:(state) => { return state.lst.beta_options },

		// sparkl_student_activities_enabled:(state) => { return state.lst.beta_options.sparkl_student_activities },
		// resource_repositories_enabled:(state) => { return state.lst.beta_options.resource_repositories },
		// new_course_index_enabled:(state) => { return state.lst.beta_options.new_course_index },

		academic_year:(state) => { return state.user_info.academic_year },
		academic_year_display:(state) => { 
			let start_year = (state.user_info.academic_year) || state.default_academic_year
			return `${start_year}-${start_year*1+1}`
		},
		signed_in:(state) => {
			return (state.user_info.user_id > 0)
		},
		// the user_info.system_role value indicates the highest role the user can adopt
		system_role:(state) => { return state.user_info.system_role },
		// but staff and admin users can adopt different roles; so usually when we want to do something that's role-based, we look at the user_info.role value, which reflects the role the user has currently chosen to adopt
		role:(state) => { return state.user_info.role },
		studentish_role:(state) => { return (state.user_info.role == 'student' || state.user_info.role == 'parent') },
		simulating_user:(state) => { return !empty(state.lst.simulated_user) },
		user_is_principal_or_ap:(state) => {
			// deal with misspellings
			return state.user_info.district_role.some(x=>(x == 'principal' || x == 'assistant principal' || x == 'principle' || x == 'assistant principle'))
		},
		backdoor_login_only:(state) => { return state.site_config.backdoor_login_only == 'yes' },
		show_all_items_when_not_signed_in:(state) => { return state.site_config.show_all_items_when_not_signed_in == 'yes' },
		use_term_mode:(state) => { return vapp.site_config.use_term_mode == 'yes' },
		small_screen:(state) => {
			// let val = vapp.$vuetify.breakpoint.height < 600 || vapp.$vuetify.breakpoint.width < 600
			// PW 8/13/2024: just check width; the height can get to < 600 pretty easily if you have a short monitor and/or if you increase the magnification of your screen.
			let val = vapp.$vuetify.breakpoint.width < 600
			return val
		},
		lti_chooser:(state) => { return vapp.app_mode == 'lti_chooser' },
		// Family view getters:
		child_count:(state) => {
			let i = 0
			for (let child_email in state.user_info.child_data) ++i
			return i
		},
		child_email_showing:(state) => {
			if (state.user_info.child_data) {
				// if user has selected a child, return it
				if (state.lst.child_email_showing && !empty(state.user_info.child_data[state.lst.child_email_showing])) {
					return state.lst.child_email_showing
				}
				// else return the first child listed
				for (let email in state.user_info.child_data) {
					return email
				}
			}
			return ''
		},
		child_data_showing:(state, getters) => {
			if (empty(getters.child_email_showing)) {
				return {}
			}
			return state.user_info.child_data[getters.child_email_showing]
		},
		// URL of websocket server
		// All prod DNSes route messages to wss://henryconnect.net/wss2
		henry_chatter_url:(state) => {
			let hc_url = 'ws://localhost:8082'
			if ('https://dev-tassle.commongoodlt.com/' == window.location.hostname) hc_url = 'wss://https://dev-tassle.commongoodlt.com//wss2'
			// prod socket messages all go though henryconnect.net
			else if ((window.location.hostname).includes('henry')) hc_url = 'wss://henryconnects.net/wss2'
			return hc_url
		},

		app_name_with_indefinite_article:(state) => {
			// In some instances, the App Title is prefixed by an indefinite article, e.g. "an Inspire system administrator"
			// In those cases, we add the article here based on a regex
			return `${/^[aeiou]/i.test(state.site_config.app_name) ? 'an' : 'a'} ${state.site_config.app_name}`
		},

		my_default_collection:(state, getters) => {
			// students don't have a default collection
			if (getters.studentish_role) return null

			if (state.user_info.user_id == 0) return null
			// we currently store the signed-in user's dummy default collection in all_courses[0]
			return state.all_courses[0]
		},

		manage_assignments:(state) => {
			// return false
			// // TEMP: only allow for this in local development
			// if (window.location.host.indexOf('localhost') == -1) return false

			return state.site_config.manage_assignments == 'yes'
		},

		// determine the current_term based on the term_settings and today's date
		current_term:(state) => {
			// return the first term for which today is within the start_date and end_date
			// note that we index terms starting at 1 (1, 2, 3, or 4)
			let today = date.format(state.now_date_obj, 'YYYY-MM-DD')
			for (let i = 0; i < state.term_settings.term_dates.length; ++i) {
				let td = state.term_settings.term_dates[i]
				if (today >= td.start_date && today <= td.end_date) {
					return (i+1)
				}
			}
		},

		activities_for_student:(state, getters) => {
			// If  role is parent, we only want to show activities for the current student...
			if (state.user_info.role == 'parent') {
				let arr = []
				for (let activity of state.my_activities) {
					let sc = getters.child_data_showing.sis_student_classes
					if (!sc || sc.findIndex(x=>x.course_code == activity.course_code) == -1) {
						continue
					}
					arr.push(activity)
				}
				return arr
			}
		},
		
		// list of the collections for courses the user is teaching/taking only, according to the sis, for the entire year
		my_sis_course_collections_all:(state, getters) => {
			let arr = []
			for (let course of state.sis_classes) {
				// filter out courses with code 'xxx'
				if (course.course_code == 'xxx') continue
				// filter out duplicates
				if (arr.find(x=>x.course_code == course.course_code)) continue
				// filter out gifted courses ?? -- no; currently (8/2024) we don't seem to be able to distinguish between honors and gifted courses
				// if (course.is_gifted_course()) continue
					
				let lp = state.all_courses.find(x=>x.course_code == course.course_code)

				// if we don't have an lp for this course_code...
				if (empty(lp)) {
					// skip it for staff/admin, but show for parent/student?
					if (state.role == 'staff' || state.role == 'admin') continue

					// no, just skip it altogether
					// continue

				} else if (lp.active == 'no') {
					// // for inactive lp's, only show to admins
					// if (!lp.user_is_lp_admin()) {
					// 	continue
					// }
				}

				arr.push(course)
			}
			return arr
		},
		
		// list of the collections for courses the user is teaching/taking only, to show in the user's favorite collections
		my_sis_course_collections_for_favorites:(state, getters) => {
			// for teachers, show all courses for the year
			if (!getters.studentish_role) return getters.my_sis_course_collections_all

			// for students/parents, for now use my_sis_course_collections_for_directives...
			return getters.my_sis_course_collections_for_directives

			// what we really want is to include courses the student is taking now or (within the last 2 weeks) or (within the next 2 weeks) -- or maybe instead we should show a toggle for term on Favorite Collections...

			// for students/parents, filter by term
			let arr = []
			for (let course of getters.my_sis_course_collections_all) {
				// let current = true
				// let today = '2024-05-01'
				let today = date.format(state.now_date_obj, 'YYYY-MM-DD')
				let current = false
				for (let i = 0; i < state.term_settings.term_dates?.length; ++i) {
					// if today is within the start_date and end_date of any term...
					let td = state.term_settings.term_dates[i]
					if (today >= td.start_date && today <= td.end_date) {
						// ...then if this course matches the term...
						if (course.class_matches_term(i+1)) {
							// this is a current course, so show it
							current = true
							break
						}
					}
				}
				if (!current) {
					// console.warn('course doesn’t match term', extobj(course))
					continue
				}

				// for parent role, also only show classes for the currently-selected student
				if (state.user_info.role == 'parent') {
					let sc = getters.child_data_showing.sis_student_classes
					if (!sc || sc.findIndex(x=>x.course_code == course.course_code) == -1) {
						continue
					}
				}

				arr.push(course)
			}
			return arr
		},

		// list of the collections for courses a student is taking now or took earlier in the year only, to show in the user's directives lists
		my_sis_course_collections_for_directives:(state, getters) => {
			// for teachers, show all courses for the year
			if (!getters.studentish_role) return getters.my_sis_course_collections_all

			// for students/parents, filter by term
			let arr = []
			for (let course of getters.my_sis_course_collections_all) {
				let current = false

				// if course matches the current term or any previous term, include it
				for (let i = getters.current_term_index; i >= 0; --i) {	// note that term 1 is current_term_index 0
					if (course.class_matches_term(i+1)) {
						current = true
						break
					}
				}

				if (!current) {
					// console.warn('course doesn’t match term', extobj(course))
					continue
				}

				// for parent role, also only show classes for the currently-selected student
				if (state.user_info.role == 'parent') {
					let sc = getters.child_data_showing.sis_student_classes
					if (!sc || sc.findIndex(x=>x.course_code == course.course_code) == -1) {
						continue
					}
				}

				arr.push(course)
			}
			return arr
		},

		current_term_index:(state, getters) => {
			let today = date.format(state.now_date_obj, 'YYYY-MM-DD')
			if (empty(state.term_settings?.term_dates) || today < state.term_settings.term_dates[0].start_date) return -1
			let i = 0
			for (; i < state.term_settings.term_dates.length; ++i) {
				// if today is within the start_date and end_date of any term, we're in that term
				let td = state.term_settings.term_dates[i]
				if (today >= td.start_date && today <= td.end_date) {
					break
				}
			}
			// if we go through the whole loop without being in any term, we'll return term_dates.length + 1
			return i
		},

		// MESSAGE CENTER RELATED GETTERS
		use_message_center:(state) => {
			return state.site_config.use_message_center == 'yes'
		},
		threaded_messages:(state) => {
			const threads = state.messages?.filter(message => message.message_level*1 === 0)
				.map(message => ({ ...message, replies: [] })) ?? []
			const level_one_messages = state.messages?.filter(message => message.message_level*1 === 1)
				.map(message => ({ ...message, replies: [] })) ?? []
			const level_two_messages = state.messages?.filter(message => message.message_level*1 === 2) ?? []

			level_two_messages.forEach(message => {
				const match = level_one_messages.find(a => a.message_id === message.parent_message_id)
				if (match) {
					match.replies.push(message)
				}
			})
			level_one_messages.forEach(message => {
				const match = threads.find(a => a.message_id === message.parent_message_id)
				if (match) {
					match.replies.push(message)
				}
			})

			return threads.map(thread => new window.Message(thread))
		},
		messages_for_student:(state, getters) => {
			// if role is parent, we only want to show messages for the selected student...
			if (state.user_info.role == 'parent') {
				const student_sourcedId = getters.child_data_showing.sourcedId
				if (!student_sourcedId) return []
				let arr = []
				for (let message of getters.threaded_messages) {
					if (message.recipients?.map(recipient => recipient.user_sourcedId)?.includes(student_sourcedId)) {
						arr.push(message)
					}
				}
				return arr
			}
		},

		unread_message_count:(state) => (course_code = "") => {
			return state.messages.filter(message =>
				!message.is_read &&
				message.message_id !== 0 &&
				(course_code == "" || message.course_code == course_code)
			).length
		},

		// have an instance-based setting for whether or not to show standards-aligned resources here (at least initially)
		immediately_show_standards_aligned_resources:(state) => {
			return state.site_config.immediately_show_standards_aligned_resources == 'yes'
		},
		
		// SHORTHAND COLOR VALUES FROM site_config
		// This was useful when migrating Henry functionality to Cureum
		primary_color:(state) => {
			return state.site_config.vue_primary_color
		},
		secondary_color:(state) => {
			return state.site_config.vue_secondary_color
		},

		show_daily_lessons:(state, getters) => {
			if (state.site_config.show_daily_lessons != 'yes') return false
			if (getters.studentish_role) return false
			return true
		},

		in_reslink_view:(state, getters) => {
			return vapp.$router.currentRoute.name == 'reslink_view'
		},

		cureum_code:(state) => {
			if (!empty(state.site_config.cureum_code)) return state.site_config.cureum_code
			else return state.site_config.app_name
		},

		favorite_collections:(state, getters) => {
			// note that, mostly for legacy purposes, this returns an array of "User_Course"s
			// console.log('-------- LOADING COLLECTIONS --------')
			let arr = []
			for (let course of state.sis_classes) {
				// filter out collections with code 'xxx'
				if (course.course_code == 'xxx') continue

				// also filter out collections in removed_my_courses
				if (state.removed_my_courses.find(x=>x == course.course_code)) continue

				if (arr.find(x=>x.course_code==course.course_code)) continue

				// if this isn't a course we have a collection for, don't show
				let c = state.all_courses.find(x=>x.course_code == course.course_code)
				if (empty(c)) continue
				// and if the collection is not active, or it's a non-agency-sanctioned course
				else if (c.active == 'no' || (c.collection_type == 'course' && !c.agency_sanctioned)) {
					// only show to people explicitly authorized as viewers (this includes admins)
					if (!c.user_can_view_lp()) continue
				}

				// If viewing as parent, only show collections for the selected child
				if (state.user_info.role === 'parent' && getters.child_data_showing?.sis_student_classes) {
					const child_in_course = getters.child_data_showing.sis_student_classes.find(x => x.course_code == course.course_code)
					if (!child_in_course) {
						continue
					}
				}

				// if viewing as parent or student, only show courses in the current term (or in the recent past or near future), which is handled by my_sis_course_collections_for_favorites
				if (state.user_info.role === 'parent' || state.user_info.role === 'student') {
					if (getters.my_sis_course_collections_for_favorites.findIndex(x=>x.course_code == course.course_code) == -1) {
						continue
					}
				}

				arr.push(course)
			}

			// add collections whose course_codes are in added_my_courses (and which we haven't yet processed above)
			for (let course_code of state.added_my_courses) {
				// skip if we already processed the class above -- the class might, e.g., have been added to the teacher's sis_classes after they explicitly added it
				if (arr.find(x=>x.course_code==course_code)) continue

				let c = state.all_courses.find(x=>x.course_code == course_code)
				if (empty(c)) continue
				else if (c.active == 'no' || (c.collection_type == 'course' && !c.agency_sanctioned)) {
					if (!c.user_can_view_lp()) continue
				}
				arr.push(new User_Course({course_code: course_code, titles: [c.title]}))
			}

			// always include my default collection at the end
			if (getters.my_default_collection) arr.push(getters.my_default_collection)

			return arr

			// NOTE: sis_classes will appear first, in alpha order, followed by added classes
		},
	},
	mutations: {
		set(state, payload) {
			// this.$store.commit('set', ['key', val])
			// update state property 'key' to value 'val'
			if (payload.length == 2) {
				state[payload[0]] = payload[1]
				return
			}

			var o = payload[0]
			var key = payload[1]
			var val = payload[2]

			// this.$store.commit('set', ['obj', 'key', val])
			// update property 'key' of 'o' to value 'val'
			if (typeof(o) == 'string') {
				if (state[o][key] == undefined) Vue.set(state[o], key, val)
				else state[o][key] = val
				return
			}

			// this.$store.commit('set', [obj, 'key', true])
			// this.$store.commit('set', [obj, ['level_1_key', 'level_2_key'], true])
			// this.$store.commit('set', [obj, 'PUSH', 1])	// push 1 onto obj, which must be an array in this case
			// update property of obj, **WHICH MUST BE PART OF STATE!**
			if (typeof(key) == 'string') {
				if (key == 'PUSH') {
					o.push(val)
				} else if (key == 'UNSHIFT') {
					o.unshift(val)
				} else if (key == 'SPLICE') {
					// if we got a fourth value in payload, add that value into the array; otherwise just take the val-th item out
					if (!empty(payload[3])) {
						o.splice(val, 1, payload[3])
					} else {
						o.splice(val, 1)
					}
				} else if (val == '*DELETE_FROM_STORE*') {
					// delete the val if it existed (if it didn't exist, we don't have to do anything)
					if (o[key] != undefined) Vue.delete(o, key)
				} else if (o[key] == undefined) {
					Vue.set(o, key, val)
				} else {
					o[key] = val
				}
			} else {
				for (var i = 0; i < key.length-1; ++i) {
					o = o[key[i]]
					if (empty(o)) {
						console.log('ERROR IN STORE.SET', key, val)
						return
					}
				}
				if (o[key[i]] == undefined) Vue.set(o, key[i], val)
				else o[key[i]] = val
			}

			// samples:
			// this.$store.commit('set', [this.exercise, ['temp', 'editing'], true])
			// this.$store.commit('set', [this.qstatus, 'started', true])
		},

		merge_site_config(state, source) {
			// deep-merge site_config values received from the server into state.site_config, so that
			// nested defaults (e.g. site_config.asset_manager.text.*) are preserved when an instance's
			// config.php only overrides some of the keys within a nested object, rather than all of them
			function merge(target, src) {
				for (let key in src) {
					let val = src[key]
					if (val !== null && typeof(val) == 'object' && !Array.isArray(val)
						&& target[key] !== null && typeof(target[key]) == 'object' && !Array.isArray(target[key])) {
						merge(target[key], val)
					} else if (target[key] === undefined) {
						Vue.set(target, key, val)
					} else {
						target[key] = val
					}
				}
			}
			merge(state.site_config, source)
		},

		add_to_array(state, payload) {
			// add to the array, checking first to make sure it's not already there
			// this.$store.commit('add_to_array', [array, old_val])
			let arr = payload[0]
			let val = payload[1]

			// if it doesn't already exist, add to the array
			if (arr.findIndex(x=>x==val) == -1) {
				arr.push(val)
			}
		},

		replace_in_array(state, payload) {
			// this.$store.commit('replace_in_array', [array, old_val, new_val])
			let arr = payload[0]

			// try to find the index of the old_val; caller can send either a value to look for directly, or a property and a value
			let i, new_val
			if (payload.length == 3) {
				let old_val = payload[1]
				new_val = payload[2]
				i = arr.findIndex(x=>x==old_val)
			} else {
				let prop = payload[1]
				let old_val = payload[2]
				new_val = payload[3]
				i = arr.findIndex(x=>x[prop]==old_val)
			}

			if (i > -1) {
				// if found, replace with new_val; have to use splice for reactive arrays (see vue documentation)
				arr.splice(i, 1, new_val)
			} else {
				// else push
				arr.push(new_val)
			}
		},

		splice_from_array(state, payload) {
			// this.$store.commit('splice_from_array', [array, old_val])
			let arr = payload[0]
			let old_val = payload[1]

			// try to find the index of the old_val
			let i = arr.findIndex(x=>x==old_val)
			if (i > -1) {
				// if found, replace with new_val; have to use splice for reactive arrays (see vue documentation)
				arr.splice(i, 1)
			}
		},

		splice_from_array_by_index(state, payload) {
			// this.$store.commit('splice_from_array', [array, old_val])
			let arr = payload[0]
			let i = payload[1]

			arr.splice(i, 1)
		},

		// consolidate students from all sis_classes into a single associative array
		process_students_from_sis_classes(state) {
			let o = {}
			for (let cl of state.sis_classes) {
				if (empty(cl.students)) continue
				for (let i = 0; i < cl.students.length; ++i) {
					let student_list = cl.students[i]
					if (empty(student_list)) continue
					for (let student of student_list) {
						if (empty(o[student.sourcedId])) {
							o[student.sourcedId] = student
							o[student.sourcedId].class_sourcedIds = [cl.class_sourcedIds[i]]
						} else {
							o[student.sourcedId].class_sourcedIds.push(cl.class_sourcedIds[i])
						}
					}
				}
			}
			state.all_students = o
		},

		trigger_course_update(state) {
			state.course_update_trigger += 1
		},

		// fns to initialize and set local_storage settings
		lst_initialize(state) {
			for (let key in state.lst) {
				let val = U.local_storage_get(state.lst_prefix + key)
				if (!empty(val)) {
					state.lst[key] = val
				}
			}

			// initialize beta_options specially, given that the options here will change periodically
			if (state.lst.beta_options) {
				// console.log(JSON.stringify(state.lst.beta_options))
				for (let key in state.default_beta_options) {
					if (state.lst.beta_options[key] === undefined) {
						Vue.set(state.lst.beta_options, key, state.default_beta_options[key])
					}
				}
			}
		},

		// this.$store.commit('lst_set', ['mc_mode', 'bubbles'])
		lst_set(state, payload) {
			let key, val
			if (typeof(payload) == 'string') {
				// if a single string value is sent in, we just save in local_storage; presumably the changed value will have been already saved via set
				U.local_storage_set(state.lst_prefix + payload, state.lst[payload])
			}

			if (Array.isArray(payload)) {
				key = payload[0]
				val = payload[1]
			} else {
				key = payload.key
				val = payload.val
			}

			// save in state
			state.lst[key] = val

			// now save in local_storage
			U.local_storage_set(state.lst_prefix + key, val)
		},

		lst_clear(state, key) {
			U.local_storage_clear(state.lst_prefix + key)
		},

		// parse site_config data
		write_site_config(state) {
			let banner_logo_text_color = U.get_contrast_color(state.site_config.banner_color || '#000000')
			let style_rules = [
				` :root { --agency-name-font-family: ${state.site_config.agency_name_font_family}; }`,
				`.k-main-wrapper .k-app-toolbar .k-cureum-logo-text { ${state.site_config.banner_logo_css} }`,
				`.k-main-wrapper .k-app-toolbar .k-cureum-logo-text, .k-main-wrapper .k-app-toolbar .k-cureum-logo-text * { color: ${banner_logo_text_color} !important; -webkit-text-fill-color: currentColor !important; }`,
				`.k-main-wrapper .k-app-toolbar .k-cureum-subdomain-text { ${state.site_config.banner_subdomain_css} }`,
				`.k-main-wrapper .k-app-toolbar .k-toolbar__logo-img { ${state.site_config.agency_img_css} }`,
				// note that k-login__logo-text and k-login__logo-img won't be used for oidc login
				`.k-login-wrapper { ${state.site_config.login_wrapper_css} }`,
				`.k-login__logo-img { ${state.site_config.login_logo_img_css} }`,
				`.k-login__logo-text { ${state.site_config.login_logo_text_css} }`,
				`.k-main-wrapper .k-app-toolbar { background-color: ${state.site_config.banner_color} !important; }`,

				`.k-favorites-dialog-inner, .k-lti-chooser-home { background-color: ${state.site_config.banner_color} !important; color: ${banner_logo_text_color} !important; border-color: ${banner_logo_text_color} !important;}`,
				`.k-favorites-dialog-inner, .k-lti-chooser-home .k-favorites-icon-holder .v-icon { color: ${banner_logo_text_color} !important; }`,

				`.k-app-toolbar-icon-btn-vx { background-color: ${state.site_config.banner_color} !important; color: ${banner_logo_text_color} !important }`,
				`.k-app-toolbar-menu-btn-vx { background-color: ${state.site_config.banner_color} !important; color: ${banner_logo_text_color} !important }`,
				`.k-app-toolbar-menu-btn-vx-current .v-btn__content { border-bottom-color: ${banner_logo_text_color} !important }`,
				`.k-toolbar__logo-img-vx { ${state.site_config.vx_banner_logo_img_css} }`,
				`.k-cureum-logo-text-vx { ${state.site_config.vx_banner_logo_text_css} }`,

				`.k-main-wrapper .k-app-toolbar .k-toolbar__username { color: ${state.site_config.banner_user_name_color}; }`,
				`.k-main-wrapper .k-app-toolbar .k-cureum-subdomain-text { color: ${state.site_config.banner_user_name_color}; }`,
				`.k-main-wrapper .k-app-toolbar .k-cureum-subdomain-text button { color: ${state.site_config.banner_user_name_color}!important; }`,
				`.k-main-wrapper .k-main-wrapper-bgd { ${state.site_config.main_background_screen_css} }`,

				`.k-main-welcome .k-main-welcome--nav-btns .k-main-welcome--nav-btn { background-color: ${state.site_config.vue_primary_color}; }`,
				`.k-main-welcome .k-main-welcome--nav-btns .k-main-welcome--nav-btn-current .v-icon { color: ${state.site_config.vue_primary_color}; }`,
				// `.k-main-wrapper .k-mini-nav .v-btn { background-color: ${state.site_config.vue_secondary_color} !important; }`,
				// `.k-main-wrapper .k-mini-nav .k-mini-nav-current-btn i { color: ${state.site_config.vue_primary_color} !important; }`,
				`.k-main-wrapper .k-mini-nav .k-mini-nav-non-current-btn { background-color: ${state.site_config.vue_primary_color} !important; }'`,

				// not sure if we will ever need these, but just in case...
				`.k-primary-color-background { background-color:${state.site_config.vue_primary_color}; }`,
				`.k-primary-color-text { color:${state.site_config.vue_primary_color}; }`,
				`.k-secondary-color-background { background-color:${state.site_config.vue_secondary_color}; }`,
				`.k-secondary-color-text { color:${state.site_config.vue_secondary_color}; }`,
			]

			$('head').append('<style type="text/css">' + style_rules.join('\n') + '</style>');

			document.title = state.site_config.index_title
			document.getElementById('agency-name-font-family').href = state.site_config.agency_name_font_family_href

			// copy grades and subjects from site_config to state, for coding convenience
			state.grades = state.site_config.grades

			vapp.$vuetify.theme.themes.dark.primary = vapp.$vuetify.theme.themes.light.primary = vapp.$vuetify.theme.defaults.dark.primary = vapp.$vuetify.theme.defaults.light.primary = state.site_config.vue_primary_color
			vapp.$vuetify.theme.themes.dark.secondary = vapp.$vuetify.theme.themes.light.secondary = vapp.$vuetify.theme.defaults.dark.secondary = vapp.$vuetify.theme.defaults.light.secondary = state.site_config.vue_secondary_color

			// write updated svg for spinner with colors provided in site_config
			let spinner_color_1 = state.site_config.spinner_color_1 || state.site_config.vue_primary_color
			let spinner_color_2 = state.site_config.spinner_color_2 || state.site_config.vue_secondary_color
			let svg = `<svg id="spinner" style="position:absolute; left:50%; top:50%; margin: -50px 0 0 -50px; transform:scale(2); background: none;" width="100px"  height="100px"  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" class="lds-ripple"><circle cx="50" cy="50" r="21.2875" fill="none" ng-attr-stroke="{{config.c1}}" ng-attr-stroke-width="{{config.width}}" stroke="${spinner_color_1}" stroke-width="5"><animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate><animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate></circle><circle cx="50" cy="50" r="38.2118" fill="none" ng-attr-stroke="{{config.c2}}" ng-attr-stroke-width="{{config.width}}" stroke="${spinner_color_2}" stroke-width="5"><animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="0s" repeatCount="indefinite"></animate><animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="0s" repeatCount="indefinite"></animate></circle></svg>`
			$('#spinner-wrapper').html(svg)

			// set state.login_msg to site_config.login_msg; that msg could get changed
			state.login_msg = state.site_config.login_msg

			// if default_collection_view_mode is set and the lst value for collection_view_mode is *not* set...
			if (empty(U.local_storage_get(state.lst_prefix + 'collection_view_mode'))) {
				// set it to the default
				state.lst.collection_view_mode = state.site_config.default_collection_view_mode
			}
			

			// NOTE: site_config can specify lesson scheme data in one (or more) of four ways:

			// 1. site_config may include a ** variant_b_lesson_master_data ** property,
			// in which case we replace window.variant_b_lesson_master_data altogether
			if (!empty(state.site_config.variant_b_lesson_master_data)) {
				window.variant_b_lesson_master_data = state.site_config.variant_b_lesson_master_data
			}

			// 2. site_config may include ** variant_b_lesson_plan_edits **
			// here, we can replace, edit, or add individual components of window.variant_b_lesson_master_data.lesson_plan
			// these components may be used in any schemes
			if (!empty(state.site_config.variant_b_lesson_plan_edits)) {
				for (let component_edits of state.site_config.variant_b_lesson_plan_edits) {
					// if lc_uuid is 'standards', that's the special 'standards_component' part of the master structure; here the instance might want to, e.g., change lc_title from 'Learning Standards' to 'Content Standards
					if (component_edits.lc_uuid == 'standards') {
						for (let key in component_edits) {
							window.variant_b_lesson_master_data.standards_component[key] = component_edits[key]
						}
						continue
					}

					// look for a master_component that matches component_edits.lc_uuid; if found...
					let master_component_index = window.variant_b_lesson_master_data.lesson_plan.findIndex(x=>x.lc_uuid == component_edits.lc_uuid)
					if (master_component_index > -1) {
						// delete the component if told to do so
						if (component_edits.delete === true) {
							window.variant_b_lesson_master_data.lesson_plan.splice(master_component_index, 1)

						// else edit specified properties
						} else {
							let master_component = window.variant_b_lesson_master_data.lesson_plan[master_component_index]
							for (let key in component_edits) {
								master_component[key] = component_edits[key]
							}
						}
					
					// if master component not found, add the new component
					} else {
						// by default add at the start, but allow for specifying to add after a default master component
						let splice_index = 0
						if (!empty(component_edits.after_lc_uuid)) {
							splice_index = window.variant_b_lesson_master_data.lesson_plan.findIndex(x=>x.lc_uuid == component_edits.after_lc_uuid)
							if (splice_index == -1) splice_index = 0
							else splice_index += 1

							delete component_edits.after_lc_uuid
						}

						window.variant_b_lesson_master_data.lesson_plan.splice(splice_index, 0, component_edits)
					}
				}
			}

			// 3. site_config may include ** variant_b_lesson_plan_schemes **
			// here, we define a whole scheme "from scratch", which could be a new scheme or overwrite one of the "base" schemes
			// see note about alt_component_data below
			if (!empty(state.site_config.variant_b_lesson_plan_schemes)) {
				for (let key in state.site_config.variant_b_lesson_plan_schemes) {
					// allow for deleting a scheme
					if (state.site_config.variant_b_lesson_plan_schemes[key] == 'DELETE') {
						// note though, that we don't completely delete it; we mark it as deleted so it doesn't show up as an option for *new* lessons. we keep it around in case there are existing lessons that use it.
						window.variant_b_lesson_master_data.lp_component_schemes[key].deleted = true
						continue
					}
					// here we just add or replace the scheme
					window.variant_b_lesson_master_data.lp_component_schemes[key] = state.site_config.variant_b_lesson_plan_schemes[key]
				}
			}

			// 4. site_config may include ** variant_b_lesson_plan_scheme_edits **
			// here, the config is specifying edits to "base" schemes defined in lessons.js
			if (!empty(state.site_config.variant_b_lesson_plan_scheme_edits)) {
				for (let key in state.site_config.variant_b_lesson_plan_scheme_edits) {
					let os = window.variant_b_lesson_master_data.lp_component_schemes[key]
					if (empty(os)) continue		// scheme not found (probably shouldn't happen)

					// copy os; then we'll re-set it below
					os = object_copy(os)
					let ns = state.site_config.variant_b_lesson_plan_scheme_edits[key]

					// replace any specified data
					if (!empty(ns.label)) os.label = ns.label
					if (!empty(ns.edit_order)) os.edit_order = ns.edit_order

					if (!empty(ns.groups)) {
						for (let ng of ns.groups) {
							let og = os.groups.find(x=>x.lc_uuid == ng.lc_uuid)
							if (empty(og)) continue
							for (let k in ng) {
								og[k] = ng[k]
							}
						}
					}

					// create or add to alt_component_data for the scheme (see note below)
					if (!empty(ns.alt_component_data)) {
						if (empty(os.alt_component_data)) os.alt_component_data = {}
						for (let ak in ns.alt_component_data) {
							if (empty(os.alt_component_data[ak])) continue
							for (let acdk in ns.alt_component_data[ak]) {
								os.alt_component_data[ak][acdk] = ns.alt_component_data[ak][acdk]
							}
						}
					}

					// preserve reactivity
					window.variant_b_lesson_master_data.lp_component_schemes[key] = os
				}
			}

			// IMPORTANT NOTE about lesson_plan components and schemes:
			// all components used in any schemes must be defined in window.variant_b_lesson_master_data.lesson_plan
			// so if, on a cureum instance, we want to add a new component for one or more schemes,
			// the component must first be defined in site_config.variant_b_lesson_plan_edits
			// then the component's lc_uuid should be added to the relevant `group` for each scheme
			// the scheme can also override properties (e.g. the lc_title or lc_guidance) for the master component

			// IMPORTANT NOTE about alt_component_data:
			// any scheme can include components with lc_uuids that map on to window.variant_b_lesson_master_data.lesson_plan's "base" components
			// furthermore, any scheme can have alt_component_data, and a scheme's alt_component_data can be created or edited via either mechanism 3 or 4 above,
			// if alt_component_data exists for a scheme, it specifies edits to the base components that should be applied only in the context of that scheme
			// These edits are merged in with the base component data
			// when the scheme is "instantiated" at the time the lesson is rendered in LessonView 
			// or edited in LessonEditorEnhanced

		},

		generate_default_collection(state) {
			// create user's "default collection" from incoming default_collection_lessons and default_collection_resources
			let dc = new Learning_Progression({
				fully_loaded: true,
				lp_id: 1,
				collection_type: 'my',
				lp_layout: 'tree',
				use_terms: false,
				use_unit_numbers: false,
				use_unit_intervals: false,
				updated_at: '2023-11-02 00:00:00',
				course_code: 'default',
				title: state.site_config.default_my_collection_label,
				color: 'default',
				units: [
					// note "magic constants" for fake lp_unit_ids for lessons, sparkls, and link resources
					new LP_Unit({lp_unit_id: 2, lp_id: 1, title: 'Lessons'}),
					new LP_Unit({lp_unit_id: 3, lp_id: 1, title: `${state.site_config.sparkl_app_name} Student Activities`, 
						text: state.site_config.default_my_collection_sparkl_unit_description.replace(/%sparkl_app_name%/g, state.site_config.sparkl_app_name)
					}),
					new LP_Unit({lp_unit_id: 4, lp_id: 1, title: 'Other Resources'}),
				],
				repo_category: '999_',
				sequence: 999
			})
			// get the lp_id value used on the server for the user's default collection (we use lp_id 1 here on the client)
			let default_collection_id = dc.default_collection_id_for_collection_asset_mapping()
			let other_collection_lessons = []
			for (let item of state.my_lessons) {
				// get the cam record for the item
				let cam = state.my_ca_mappings.find(x=>x.asset_id == item.lesson_id)

				// if the item is actually from another collection, add to a special folder
				if (cam && cam.collection_id != default_collection_id) other_collection_lessons.push(item)
				// else it was created in the default collection
				else {
					dc.units[0].lessons.push(item)
					dc.units[0].add_item_to_folder({parent_folder_id:'top', type:'lesson', resource_id:item.lesson_id})
				}
			}

			let other_collection_sparkl = []
			let other_collection_resources = []
			for (let item of state.my_resources) {
				// get the cam record for the item
				let cam = state.my_ca_mappings.find(x=>x.asset_id == item.resource_id)

				if (item.type == 'sparkl') {
					// if the item is actually from another collection, add to a special folder
					if (cam && cam.collection_id != default_collection_id) other_collection_sparkl.push(item)
					// else it was created in the default collection
					else {
						dc.units[1].resources.push(item)
						dc.units[1].add_item_to_folder({parent_folder_id:'top', type:'resource', resource_id:item.resource_id})
					}

				} else {
					// if the item is actually from another collection, add to a special folder
					if (cam && cam.collection_id != default_collection_id) other_collection_resources.push(item)
					// else it was created in the default collection
					else {
						dc.units[2].resources.push(item)
						dc.units[2].add_item_to_folder({parent_folder_id:'top', type:'resource', resource_id:item.resource_id})
					}
				}
			}

			// if we got other_collection_xxx's, make a folder for each one
			if (other_collection_lessons.length > 0) {
				dc.units[0].lessons = dc.units[0].lessons.concat(other_collection_lessons)
				dc.units[0].create_resource_folder({parent_folder_id:'top', title:'Lessons that you’ve created from other collections', folder_id:`default_content_other_collection_lessons_${state.user_info.user_id}`, items:other_collection_lessons})
			}
			if (other_collection_sparkl.length > 0) {
				dc.units[1].resources = dc.units[1].resources.concat(other_collection_sparkl)
				dc.units[1].create_resource_folder({parent_folder_id:'top', title:`${state.site_config.sparkl_app_name} Student Activities that you’ve created from other collections`, folder_id:`default_content_other_collection_sparkl_${state.user_info.user_id}`, items:other_collection_sparkl})
			}
			if (other_collection_resources.length > 0) {
				dc.units[2].resources = dc.units[2].resources.concat(other_collection_resources)
				dc.units[2].create_resource_folder({parent_folder_id:'top', title:'Resources that you’ve created from other collections', folder_id:`default_content_other_collection_resources_${state.user_info.user_id}`, items:other_collection_resources})
			}
			
			// note that everything will get sorted by CollectionResourceFolder when the default collection is viewed

			// replace or add default collection in all_courses
			if (state.all_courses[0] && state.all_courses[0].lp_id == 1) {
				state.all_courses.splice(0, 1, dc)
			} else {
				state.all_courses.unshift(dc)
			}
		},

		// this fn is set to run once a minute by initialize_app
		set_now_date(state) {
			// use now_date_obj everywhere instead of Date(), so that we can simulate a certain date for demo purposes
			// try to get simulated_date from lst
			state.simulated_date = state.lst.simulated_date		// '2023-02-04'
			if (!state.simulated_date) state.now_date_obj = new Date()
			else state.now_date_obj = date.parse(state.simulated_date + ' ' + date.format(new Date(), 'HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss')
			
			// set now_date_string and old_threshold_date_string to the dates format needed to compare to lesson/activity dates
			state.now_date_string = date.format(state.now_date_obj, 'YYYY-MM-DD')
			state.old_threshold_date_timestamp = Math.round((state.now_date_obj - 7*24*60*60*1000) / 1000)
			state.old_threshold_date_string = date.format(new Date(state.now_date_obj - 7*24*60*60*1000), 'YYYY-MM-DD')	// one week prior to now
		},

		// this adds the activity if it's new, or replaces it if it already exists
		add_to_my_activities(state, activity) {
			let msg = 'add_to_my_activities: '
			let index = state.my_activities.findIndex(x => x.activity_id == activity.activity_id)
			if (index == -1) {
				msg += ' / pushing to my_activities'
				state.my_activities.push(activity)
			} else {
				msg += ' / splicing in my_activities'
				state.my_activities.splice(index, 1, activity)
			}

			if (activity.collection_id) {
				if (!state.my_activities_by_course[activity.collection_id]) {
					Vue.set(state.my_activities_by_course, activity.collection_id, [])
				}
				let index = state.my_activities_by_course[activity.collection_id].findIndex(x => x.activity_id == activity.activity_id)
				if (index == -1) {
					msg += ' / pushing to my_activities_by_course ' + activity.collection_id
					state.my_activities_by_course[activity.collection_id].push(activity)
				} else {
					msg += ' / splicing in my_activities_by_course ' + activity.collection_id
					state.my_activities_by_course[activity.collection_id].splice(index, 1, activity)
				}
			}
			
			// console.warn(msg)
		},

		remove_from_my_activities(state, activity) {
			let index = state.my_activities.findIndex(x => x.activity_id == activity.activity_id)
			state.my_activities.splice(index, 1)

			if (activity.collection_id && state.my_activities_by_course[activity.collection_id]) {
				let index = state.my_activities_by_course[activity.collection_id].findIndex(x => x.activity_id == activity.activity_id)
				state.my_activities_by_course[activity.collection_id].splice(index, 1)
			}
		},

		// any time a content item is updated, this should be called to make sure that the item is updated everywhere in state
		update_content_item(state, item) {
			// note that the incoming item MUST be an instantiated instance of the Lesson, Resource, or Activity class
			// note that activities are updated via add_to_my_activities above, and that resources associated with activities should be updated via separate calls to update_content_item for the associated resource
			// TODO?: items can be found in lessons too.....
			let msg = 'update_content_item: '

			// if the item is a lesson...
			if (item.lesson_id) {
				msg += 'lesson'
				// look in my_lessons
				let i = state.my_lessons.findIndex(x=>x.lesson_id == item.lesson_id)
				if (i > -1) {
					msg += ' / found in my_lessons ' + i
					state.my_lessons.splice(i, 1, item)
				}
				
				// look in course units
				for (let c of state.all_courses) {
					if (c.units.length == 0) continue
					for (let u of c.units) {
						let i = u.lessons.findIndex(x=>x.lesson_id == item.lesson_id)
						if (i > -1) {
							msg += ' / found in course ' + c.title
							u.lessons.splice(i, 1, item)
						}
					}
				}

			// if the item is a resource...
			} else if (item.resource_id) {
				msg += 'resource'
				// look in my_resources
				let i = state.my_resources.findIndex(x=>x.resource_id == item.resource_id)
				if (i > -1) {
					msg += ' / found in my_resources ' + i
					state.my_resources.splice(i, 1, item)
				}
				
				// look in course units
				for (let c of state.all_courses) {
					if (c.units.length == 0) continue
					for (let u of c.units) {
						let i = u.resources.findIndex(x=>x.resource_id == item.resource_id)
						if (i > -1) {
							msg += ' / found in course ' + c.title
							u.resources.splice(i, 1, item)
						}
					}
				}
			}
			
			// console.warn(msg)
		},

		update_collection_type(state, { index, lp }) {
			if (state.all_courses[index]) {
				state.all_courses[index].collection_type = lp.collection_type
				state.all_courses[index].updated_at = lp.updated_at
			}
		},

	},
	actions: {
		initialize_app({state, commit, dispatch}, payload) {
			// detect when user touches screen
			// https://codeburst.io/the-only-way-to-detect-touch-with-javascript-7791a3346685
			window.addEventListener('touchstart', function on_first_touch() {
				vapp.$store.commit('set', ['user_is_touching', true])
				// we only need to know once that a human touched the screen, so we can stop listening now
				window.removeEventListener('touchstart', on_first_touch, false);
			}, false);

			commit('set_now_date')
			setInterval(() => { commit('set_now_date') }, 60*1000)

			// if we're simulating another user, send in the simulated user's email here
			if (state.lst.simulated_user) payload.simulated_user = state.lst.simulated_user

			return new Promise((resolve, reject)=>{
				// BYPASS INITIALIZATION -- note that you won't actually be able to do anything nefarious if this code runs, because the server will know you're not an admin
				if (false) {
					commit('set', ['user_info', new User_Info({
						user_id: 1,
						first_name: 'Pepper',
						last_name: 'Williams',
						email: 'pw@pw.com',
						system_role: 'admin',
						role: 'admin',
					})])
					resolve('main')
					return
				}

				U.ajax('initialize_app', payload, result=>{
					if (result.status != 'ok') {
						console.log('Error in initialization!')
						reject()
						return
					}
					
					if (state.lst.simulated_user) {
						state.simulated_user_info = result.simulated_user_info
						if (!state.simulated_user_info) {
							vapp.$inform(`We were not able to simulate the chosen user ${state.lst.simulated_user}.`)
							commit('lst_set', ['simulated_user', ''])
						} else {
							state.actual_user_info = result.user_info
							result.user_info = result.simulated_user_info
						}
					}

					if (result.site_config) {
						// deep-merge, so that we retain default values set above (including nested ones)
						// that aren't explicitly overridden by the instance's config.php
						commit('merge_site_config', result.site_config)
					} else {
						console.error('site_config not received!')
					}
					commit('write_site_config')

					// if we're using the new 'vx' navigation scheme, update the router
					if (state.site_config.use_vx_nav) {
						window.use_vx_routes()
					}

					// if we're simulating another user and enable_sandbox_simulate_role_menu is 'yes',
					if (state.lst.simulated_user && state.site_config.enable_sandbox_simulate_role_menu == 'yes') {
						// make sure we go to 'favorites' for the course view, and make sure that the courseindex page is reset
						state.lst.course_index_view_flavor =  'favorites'
						state.lst.my_index_view_flavor =  'index'
						state.lst.repo_index_view_flavor =  'index'
						state.lst.courseindex_opened_category = null
						state.lst.courseindex_opened_subcats = {}

						// and show an alert
						vapp.$alert({
							title: 'Heads Up!',
							text: 'Please do NOT make changes or create content while simulating this user role, as such changes would impact other sandbox users using the role simulation functionality.<div class="mt-2">(But please <b>DO</b> make changes and create content when viewing the sandbox using your Demo Staff Account!)</div>',
							acceptText: 'Got It!',
							acceptIcon: 'fas fa-face-smile'
						})
					}
		
					// result should always include the google_client_id (though it could be empty)
					commit('set', ['google_client_id', result.google_client_id])

					// include the term_settings from config file
					if (result.term_settings) {			
						commit('set', ['term_settings', result.term_settings])
					}

					state.froala_key = result.froala_key

					commit('set', ['available_academic_years', result.available_academic_years])
					commit('set', ['default_academic_year', result.default_academic_year])
					commit('set', ['allow_academic_year_change_for_all_staff', result.allow_academic_year_change_for_all_staff])
					commit('set', ['todo_user_group_divisions', result.todo_user_group_divisions ?? {}])
					commit('set', ['todo_user_group_schools', result.todo_user_group_schools ?? {}])
					commit('set', ['subjects', result.subjects])


					// if we didn't receive user_info, the user is not already logged in...
					if (empty(result.user_info)) {
						// if user isn't already logged in and get string includes 'login', go right to login (after first clearing the login flag)
						if (U.check_get_string_param('login')) {
							U.clear_location_search()
							vapp.redirect_to_login()
							return
						}
						// create a dummy user_info record
						commit('set', ['user_info', new User_Info()])

						// restore added_my_courses from localstorage; we may want to do other things here
						commit('set', ['added_my_courses', state.lst.local_added_my_courses])


						// handle logging in with a token
						// if we just attempted a token signin...
						if (result.token_result) {
							// for token errors, we just inform the user of the error and proceed with initialization
							if (result.token_result != 'ok') {
								console.log(result.token_result)
								let msg = 'The one-time sign-in link you clicked did not work'
								if (result.token_result == 'token_expired') msg += ', because the link has expired'
								else if (result.token_result == 'token_not_found') msg += ', possibly because the link was already used once'
								else if (result.token_result == 'head_request') msg += ' (bad request type)'	// this should never happen in the real world
								msg += '.'
								// open the sign in dialog after they dismiss the error message
								vapp.$alert({title:'<span class="red--text text--darken-2">Sign-In Link Error</span>', text:msg}).then(U.clear_location_search())
							} 
						}

						// if we receive a login_error, the user tried to log in directly to cureum with a username/password and didn't succeed, so go back to login mode and show the error
						if (!empty(result.login_error)) {
							commit('set', ['login_error', result.login_error])
							resolve('login')
							return
						}

						// else if site_config.down_until is set, simulate a login_error with down_until (but let the user sign in using a magic password)
						if (!empty(state.site_config.down_until)) {
							commit('set', ['login_error', `${state.site_config.app_name} is temporarily down for maintenance. We should be back online by <b>${state.site_config.down_until}</b>.`])
							resolve('login')
							return
						}

						// if we're here and site_config.require_sign_in is 'yes' or show_signin_on_load is 'yes', resolve to the login screen
						if (state.site_config.require_sign_in == 'yes' || state.site_config.show_signin_on_load == 'yes') {
							// note, though, that the logic in App.vue may show the home dialog instead for some config combinations
							resolve('login')
							return
						}

					} else {
						// if we get to here, user is already logged in; clear the search string if necessary
						if (U.check_get_string_param('login')) U.clear_location_search()
						
						// else we received user_info.  if we have a simulated_role in lst, add it to user_info; if this is '' (the default value), role will be set to system_role
						result.user_info.role = state.lst.simulated_role
						commit('set', ['user_info', new User_Info(result.user_info)])
	
						if (result.token_result) {
							// if result.token_result is set, offer to let the user change their password
							let msg = 'You have been signed in by the one-time link you clicked. Would you like to create or change your ' + state.site_config.app_name + ' password at this time?'
							// Confirmation with property overrides
							vapp.$confirm({
								text: msg,
								acceptText: 'Change Password',
								cancelText: 'No thank you',
								dialogMaxWidth: 600,
							}).then(y => {

								vapp.$prompt({
									title: 'Change Password',
									text: 'Enter the new password you would like to use for your <nobr>' + state.site_config.app_name + '</nobr> account:',
									password: true,
									acceptText: 'Use this password',
								}).then(password => {
									if (!empty(password)) {
										vapp.$prompt({
											title: 'Confirm New Password',
											text: 'Please confirm the new password you just entered:',
											password: true,
											acceptText: 'Confirm and Save new password',
										}).then(new_password => {
											if (password != new_password) {
												vapp.$alert('The two passwords you entered do not match!')// .then(x=>this.change_password())
												return
											}
					
											let payload = {
												user_id: state.user_info.user_id,
												email: state.user_info.email,
												password: new_password,
											}
					
											U.loading_start()
											U.ajax('change_password', payload, result=>{
												U.loading_stop()
												if (result.status != 'ok') {
													console.log('Error in admin ajax call'); vapp.ping(); return;
												}
					
												vapp.$alert({title:'Success!', text:'Password changed.'})
											});
					
										}).catch(n=>{console.log(n)}).finally(f=>{})
									}
								}).catch(n=>{console.log(n)}).finally(f=>{})
													
							}).catch(n=>{console.log(n)}).finally(f=>{
								// regardless of which option is chosen, call clear_login_token service to clear the login token now
								U.ajax('clear_login_token', {user_id: state.user_info.user_id, email:payload.email, token:payload.token})
							})
						}

						// if the user signed in via cureum, we receive a session_id when the user first signs in; set it here in U and in localstorage
						// for OIDC login, we handle this in app->check_session
						if (result.session_id) {
							U.session_id = result.session_id
							U.local_storage_set('gaconnects_session_id', U.session_id)
						}

						// if we just signed in with a password, reload the window, because we load some things differently depending on whether you're signed in or not
						if (!empty(payload.password) || !empty(payload.google_id_token)) {
							document.location.reload()
							return
						}
					}
					if (state.user_info.user_id) U.ajax('set_last_session_id', {user_id: state.user_info.user_id, session_id: U.session_id})

					// save result.new_user_created to state so that App.vue can deal with it (this doesn't work exactly as originally intended as of 1/18/2026, but we handle it in App.vue)
					state.new_user_created = result.new_user_created
					// to test...
					// if (state.user_info.user_id) state.new_user_created = 'yes'

					resolve('main')
				});
			})
		},

		synch_user_activities_with_sparkl_server({state, commit, dispatch}) {
			return new Promise((resolve, reject)=>{
				U.ajax('synch_user_activities_with_sparkl_server', {user_id: state.user_info.user_id}, result=>{
					if (result.status != 'ok') {
						console.log('Error in synch_user_activities_with_sparkl_server!')
						reject()
						return
					}

					// on return, update in the client any new or updated user my_resources and my_ca_mappings
					if (result.updated_resources) {
						for (let resource_data of result.updated_resources) {
							let i = state.my_resources.find(x=>x.resource_id == resource_data.resource_id)
							if (i > -1) {
								state.my_resources.splice(i, 1, new Resource(resource_data))
							} else {
								state.my_resources.push(new Resource(resource_data))
							}
						}
					}
					if (result.added_ca_mappings) {
						for (let cam of result.added_ca_mappings) {
							state.my_ca_mappings.push(cam)
						}
					}

					// re-generate the default collection to add any newly-added activities
					commit('generate_default_collection')

					// resolve with the list of updated resources
					resolve(result.updated_resources)
				})
			})
		},

		set_subdomain({state, commit}, sd) {
			// console.warn('set_subdomain')
			// sd will be a subdomain "code" harvested from the route
			// if incoming sd is empty...
			if (empty(sd)) {
				// if we have a subdomain in lst, use it
				if (!empty(state.lst.current_subdomain)) sd = state.lst.current_subdomain
				// else return (do nothing)
				else return
			}

			// if sd matches the subdomain we're already viewing, return
			if (state.subdomain_data?.subdomain == sd) return

			// if we get to here, we need to pull the subdomain data from the db
			// console.warn('looking up subdomain: ' + sd)

			// get the subdomain data from the db
			U.ajax('get_subdomain_data', {subdomain: sd}, result=>{
				if (result.status != 'ok') {
					commit('lst_clear', 'current_subdomain')
					console.log('Error retrieving subdomain'); vapp.ping(); return;
				}

				if (result.bad_subdomain) {
					// fail silently if it isn't a valid subdomain (?)
					console.error('Bad subdomain: ' + sd)
					return
				}

				state.subdomain_data = result.subdomain_data

				// save in lst
				commit('lst_set', ['current_subdomain', sd])
			});
		},

		get_classes({state, commit, dispatch, getters}) {
			return new Promise((resolve, reject)=>{
				let payload
				// if signed in...
				if (getters.signed_in) {
					payload = {
						user_id: state.user_info.user_id,
						system_role: state.user_info.system_role,	// we might be simulating another user; if so, for get_classes, we want to simulate that other user's system_role and role
						role: state.user_info.role,
					}

					// also add modified list of oidc_data.course_codes	"CourseCodes": "23.1050000,23.0050000,00.0000009,00.0000013,27.1150000,27.0150000,23.1016000,23.0016000,41.0150000,45.0050000",
					if (state.user_info.oidc_data?.course_codes) {
						let arr = state.user_info.oidc_data.course_codes.split(',')
						for (let i = 0; i < arr.length; ++i) {
							// course codes in the cureum DB are always (hopefully) coded with 4 digits after the period
							arr[i] = arr[i].replace(/(\.\d\d\d\d).*/, '$1')
						}
						payload.oidc_course_codes = arr
					}

					// for students, send in sis_user_sourcedId and sis_class_sourcedIds
					if (state.user_info.system_role == 'student') {
						payload.sis_user_sourcedId = state.user_info.sis_user_sourcedId
						payload.sis_class_sourcedIds = state.user_info.sis_class_sourcedIds
					}

				// else not signed in...
				} else {
					// currently we just resolve, because we're not allowing users to add courses locally...
					state.lst.unsigned_index_view_flavor = 'lpindex'
					state.loaded_classes = true
					resolve()
					return
					// ... but we could send added_my_courses, which will have been retrieved from localstorage, if we have any
					if (state.added_my_courses.length == 0) {
						state.lst.unsigned_index_view_flavor = 'lpindex'
						state.loaded_classes = true
						resolve()
						return
					}
					// otherwise send added_my_courses in
					payload = {
						added_my_courses: state.added_my_courses
					}
				}

				U.loading_start()
				U.ajax('get_classes', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.error('Error retrieving class data')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// add added_my_courses and removed_my_courses
					if (!empty(result.added_my_courses)) commit('set', ['added_my_courses', result.added_my_courses])
					if (!empty(result.removed_my_courses)) commit('set', ['removed_my_courses', result.removed_my_courses])

					// if we didn't get any sis_classes OR added_my_courses, set unsigned_index_view_flavor to 'lpindex', since we don't have any classes to show
					if (result.classes.length == 0 && state.added_my_courses.length == 0) {
						state.unsigned_index_view_flavor = 'lpindex'

					} else {
						// for some instances (e.g. Henry) we will get a full sis record for each class; for others (e.g. Inspire) we only get a course_code for each class; 
						// use sis_classes and the User_Course model to keep things consistent either way
						if (typeof(result.classes[0]) == 'string') {
							for (let course_code of result.classes) {
								// only push here if we actually got an LP for each one
								let lp = result.learning_progressions.find(x=>x.course_code==course_code)
								if (!lp) continue

								let teachers = []
								if (state.user_info.role === 'parent') {
									teachers = result.teachers[course_code]
								}

								commit('set', [state.sis_classes, 'PUSH', new window.User_Course({
									course_code: course_code,
									titles: [lp.title],
									teachers: [teachers]
								})])
							}
						} else {
							// sort sis_classes by title (added_my_courses will appear after these in the my courses list)
							result.classes.sort((a,b) => {
								if (a.titles[0] > b.titles[0]) return 1
								if (a.titles[0] < b.titles[0]) return -1
								return 0
							})
							for (let class_data of result.classes) {
								commit('set', [state.sis_classes, 'PUSH', new window.User_Course(class_data)])
							}
							// for teachers, create object with all students here
							if (state.user_info.role == 'staff') {
								commit('process_students_from_sis_classes')
							}
						}
					}

					// process incoming lp's
					for (let lp_data of result.learning_progressions) {
						// if lp_data includes an "active" flag, set cmap_specified to true
						if (lp_data.active) lp_data.cmap_specified = true

						let lp = state.all_courses.find(x=>x.course_code==lp_data.course_code)
						if (empty(lp)) {
							lp = new Learning_Progression(lp_data)
							commit('set', [state.all_courses, 'PUSH', lp])
						}
					}

					// if we have sis_classes that we didn't get LPs for, they are probably courses the teacher may be teaching that the agency hasn't coded LPs for; add "fake" LPs for these
					for (let sis_class of state.sis_classes) {
						let lp = state.all_courses.find(x=>x.course_code==sis_class.course_code)
						if (!lp) {
							lp = new Learning_Progression({
								course_code: sis_class.course_code,
								title: sis_class.title,
								collection_type: 'course',
							})
							commit('set', [state.all_courses, 'PUSH', lp])
						}
					}

					// store my_lessons, my_resources, and my_ca_mappings, then generate default collection for non-studentish users
					if (getters.signed_in) {
						for (let item_data of result.my_lessons) state.my_lessons.push(new Lesson(item_data))
						for (let item_data of result.my_resources) state.my_resources.push(new Resource(item_data))
						commit('set', ['my_ca_mappings', result.my_ca_mappings])
						
						// if we're managing assignments, create activity records for incoming ca_mappings for resources in course collections
						state.my_activities_by_course = {}
						state.my_activities = []
						if (getters.manage_assignments) {
							if (result.my_ca_mappings) {
								for (let o of result.my_ca_mappings) {
									// skip default collection mappings
									if (o.collection_id.includes('default_collection')) continue
									// create an Activity and add it to my_activities/my_activities_by_course. Note that the user could have some my_ca_mappings that go with resources from repos, and that therefore aren't actually assignable activities; but it shouldn't hurt anything to process these in this way, and we might not know at this time which collections are actually courses.
									if (o.asset_type == 'resource') {
										o = new Activity(o)
										commit('add_to_my_activities', o)
									}
								}
							}

							// set my_activity_results if received
							let o = {}
							if (result.my_activity_results) for (let activity_id in result.my_activity_results) o[activity_id+''] = new Activity_Result(result.my_activity_results[activity_id])
							state.my_activity_results = o
						}
	
						if (!getters.studentish_role) {
							commit('generate_default_collection')
						}
					}

					// if teacher, process incoming coteacher info
					if (state.user_info.role == 'staff' && result.my_coteachers) {
						for (let coteacher of result.my_coteachers) {
							commit('set', [state.my_coteachers, 'PUSH', new Coteacher(coteacher)])
						}
						for (let course of result.my_coteaching_courses) {
							commit('set', [state.my_coteaching_courses, 'PUSH', course])
						}
					}

					commit('set', ['loaded_classes', true])

					resolve(result)
				});
			})
		},

		change_academic_year({state, commit, dispatch}, academic_year) {
			let payload = {
				user_id: state.user_info.user_id,
				academic_year: academic_year,
			}
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('change_academic_year', payload, result=>{
					state.user_info.academic_year = academic_year
					document.location.reload()

					U.loading_stop()

					resolve()
				});
			})
		},

		update_my_courses({state, commit, dispatch, getters}, payload) {
			// payload should include `course_code` and `action`, which should be `add` or `remove`
			return new Promise((resolve, reject)=>{
				// add or remove course to/from added_my_courses and removed_my_courses
				if (payload.action == 'add') {
					// but don't add to added_my_courses if the course is in sis_classes
					if (!state.sis_classes.find(x=>x.course_code==payload.course_code)) {
						commit('set', [state.added_my_courses, 'PUSH', payload.course_code])
					}
					commit('splice_from_array', [state.removed_my_courses, payload.course_code])
				} else {
					commit('set', [state.removed_my_courses, 'PUSH', payload.course_code])
					commit('splice_from_array', [state.added_my_courses, payload.course_code])
				}

				// if user isn't signed in, save to local storage
				if (!getters.signed_in) {
					// we only need to save added_my_courses to ls
					commit('lst_set', ['local_added_my_courses', state.added_my_courses])

				// else send to server
				} else {
					// add user_id to payload
					payload.user_id = state.user_info.user_id
					U.loading_start()
					U.ajax('update_my_courses', payload, result=>{
						U.loading_stop()
						if (result.status != 'ok') {
							vapp.$alert('Error updating My Courses.')
							vapp.ping()		// call ping to check if the session is expired
							reject()
							return
						}
						resolve()
					});
				}
			})
		},

		get_learning_progression({state, commit, dispatch, getters}, param) {
			let course_code, lp_id, retrieve_full_lessons, force_reload, retrieve_summary_only, retrieve_minimal_data
			// if param is an object, extract course_code and retrieve_full_lessons params
			if (typeof(param) == 'object') {
				course_code = param.course_code
				lp_id = param.lp_id
				retrieve_full_lessons = param.retrieve_full_lessons		// this should be true (boolean) or false/undefined (default)
				force_reload = param.force_reload		// this should be true (boolean) or false/undefined (default)
				retrieve_summary_only = param.retrieve_summary_only
				retrieve_minimal_data = param.retrieve_minimal_data
			} else {
				// else param is the course_code
				course_code = param
			}

			let payload = {}
			payload.user_id = state.user_info.user_id
			if (!empty(course_code)) payload.course_code = course_code
			if (!empty(lp_id)) payload.lp_id = lp_id
			if (retrieve_summary_only) payload.retrieve_summary_only = retrieve_summary_only	// this makes it so that we retrieve the summary even if the user doesn't have admin rights

			// if we just need to retrieve the summary info/minimal data for the lp, set params accordingly
			if (retrieve_summary_only || retrieve_minimal_data) {
				payload.retrieve_resources = 'no'
				payload.retrieve_resource_collections = 'no'
				payload.retrieve_case = 'no'
				payload.retrieve_professional_development_resources = 'no'
				payload.no_lessons = 'true'

			// else "normal" operation
			} else {
				// get resources and resource_collections for all users, including parents/students, since they might have access to these resources if they're enrolled in the course
				// (LpUnitResourceCollectionTree will determine exactly what they have access to)
				payload.retrieve_resources = 'yes'
				payload.retrieve_resource_collections = 'yes'

				// for teachers, retrieve additional info that students don't need
				if (state.user_info.role == 'staff' || state.user_info.role == 'admin') {
					payload.retrieve_case = 'yes'
					if (state.site_config.show_pd == 'yes') {
						payload.retrieve_professional_development_resources = 'yes'
					} else {
						payload.retrieve_professional_development_resources = 'no'
					}
				}

				// if retrieve_full_lessons isn't explicitly set to true, send a parameter indicating that we should *not* load full lessons 
				// (see get_learning_progression; the name of the param is unfortunate but I don't want to change it at this time)
				if (!retrieve_full_lessons) payload.no_lessons = 'true'
			}

			// NOTE: by default, we will load all units, resources, etc.
			// we only "lazy load" the collection if retrieve_minimal_data is true

			return new Promise((resolve, reject)=>{
				// console.warn(payload)
				let c = state.all_courses.find(o=>o.course_code == course_code)
				// if we already have the LP fully loaded, and force_reload isn't true, don't replace it
				if (!force_reload && !empty(c)) {
					// if we're only getting the LP's "shell", check fully_loaded; otherwise units_fully_loaded
					let already_loaded
					if (retrieve_summary_only || retrieve_minimal_data) already_loaded = c.fully_loaded
					else already_loaded = c.units_fully_loaded()

					if (already_loaded) {
						resolve(true)
						return
					}
				}

				U.loading_start()
				U.ajax('get_learning_progression', payload, result=>{
					U.loading_stop()

					if (result.status == 'not_authenticated') {
						resolve('not_authenticated')
						return
					}

					if (result.status == 'no_admin_rights') {
						resolve('no_admin_rights')
						return
					}

					// if not found, create a "stub" lp for the course
					if (result.status == 'not_found') {
						resolve(false)
						return
					}

					if (result.status != 'ok') {
						console.log('Error retrieving curriculum map')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}
					// console.log('get_learning_progression: lp data for course_code ' + course_code, result)

					// if we're just retrieving a summary, return the lp data
					if (retrieve_summary_only) {
						resolve(new Learning_Progression(result.learning_progression))
						return
					}

					console.log('get_collection returned data size: ' + (JSON.stringify(result.learning_progression).length/1000).toFixed(1) + 'K')
					
					// if the lp already exists, don't do anything, unless force_reload is true (it may have been loading via another service prior to this fn running)
					let i = state.all_courses.findIndex(o=>o.course_code == course_code)
					if (i == -1 || state.all_courses[i].units.length == 0 || state.all_courses[i].fully_loaded != true || force_reload == true) {
						// set fully_loaded to true
						result.learning_progression.fully_loaded = true

						// if result.learning_progression includes an "active" flag, set cmap_specified to true
						if (result.learning_progression.active) result.learning_progression.cmap_specified = true

						let lp = new Learning_Progression(result.learning_progression)

						if (i == -1) {
							// push onto all_courses
							commit('set', [state.all_courses, 'PUSH', lp])
						} else {
							let existing_collection = state.all_courses[i]
							// if the existing_collection "shell" has been fully_loaded, we may have loaded some, but not all, units, which could happen, e.g., if we initiated search after viewing one unit
							if (existing_collection.fully_loaded) {
								// in this case, *don't* replace the LP; just replace the units that weren't previously fully-loaded
								// this is important because we don't want to replace the resource and lesson objects for already-loaded units
								for (let i = 0; i < existing_collection.units.length; ++i) {
									if (!existing_collection.units[i].fully_loaded) {
										existing_collection.units.splice(i, 1, lp.units[i])
									}
								}
							
							// else the shell wasn't fully-loaded, so just replace the whole collection
							} else {
								state.all_courses.splice(i, 1, lp)
							}
						}
					}

					resolve(true)
				});
			})
		},

		// this allows us to "lazy load" units in typical usage (see Collection.vue / CollectionUnit.vue)
		get_full_units({state, commit, dispatch, getters}, params) {
			let payload = {}
			payload.user_id = state.user_info.user_id
			payload.lp_id = params.lp_id
			payload.lp_unit_ids = params.lp_unit_ids

			// get resources and resource_collections for all users, including parents/students, since they might have access to these resources if they're enrolled in the course
			// (LpUnitResourceCollectionTree will determine exactly what they have access to)
			payload.retrieve_resources = 'yes'
			payload.retrieve_resource_collections = 'yes'

			// for teachers, retrieve additional info that students don't need
			if (state.user_info.role == 'staff' || state.user_info.role == 'admin') {
				payload.retrieve_case = 'yes'
				if (state.site_config.show_pd == 'yes') {
					payload.retrieve_professional_development_resources = 'yes'
				} else {
					payload.retrieve_professional_development_resources = 'no'
				}
			}

			return new Promise((resolve, reject)=>{
				// console.warn(payload)
				let collection = state.all_courses.find(o=>o.lp_id == params.lp_id)
				// this should only be called when the collection "shell" has already been loaded; if not return an error
				if (empty(collection)) {
					console.error(`get_full_unit called, but collection ${payload.lp_id} not yet loaded`)
					resolve(false)
					return
				}

				// note that even if the unit was previously loaded, we will reload if this is called
				// U.loading_start()
				U.ajax('get_learning_progression', payload, result=>{
					// U.loading_stop()

					if (result.status == 'not_authenticated') {
						resolve('not_authenticated')
						return
					}

					if (result.status == 'no_admin_rights') {
						resolve('no_admin_rights')
						return
					}

					// if not found, create a "stub" lp for the course
					if (result.status == 'not_found') {
						resolve(false)
						return
					}

					if (result.status != 'ok') {
						console.log('Error retrieving curriculum map')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					console.log('get_full_unit returned data size: ' + (JSON.stringify(result.units).length/1000).toFixed(1) + 'K', result)
					
					// create/recreate the unit in collection
					for (let unit_data of result.units) {
						let i = collection.units.findIndex(x=>x.lp_unit_id == unit_data.lp_unit_id)
						if (i == -1) {
							// shouldn't happen
							console.error('get_full_unit: couldn’t find unit...')
							resolve(false)
							return
						}

						// transfer these state values before creating new LP_Unit
						unit_data.full_description_showing = collection.units[i].full_description_showing
						unit_data.full_description_height = collection.units[i].full_description_height

						let unit = new LP_Unit(unit_data)

						collection.units.splice(i, 1, unit)
					}
					
					resolve(true)
				});
			})
		},

		save_learning_progression({state, commit, dispatch, getters}, lp) {
			// allow for a 'no_loader' param: this.$store.dispatch('save_learning_progression', {no_loader:true, lp:this.collection})
			let show_loader = true
			if (lp.no_loader === true) {
				show_loader = false
				lp = lp.lp
			}
			// when moving resources out of shared lps, bypass client checkout on the SOURCE collection, e.g. Unpublished Content {no_lock: true, lp: this.selected_collection}
			// server save_learning_progression service will still aquire a lock
			let bypass_checkout = false
			if (lp.no_lock === true) {
				bypass_checkout = true
				lp = lp.lp
			}

			let lp_data = lp.copy_for_save()

			// if lp.unit_index_to_save is set...
			let unit_index_to_save = lp.unit_index_to_save ?? -1
			delete lp.unit_index_to_save	// remove from the lp object
			// if it's -2, don't save any units
			if (unit_index_to_save == -2) {
				lp_data.units = 'no_unit_save'
			
			// else we only want to save the unit_index_to_save-th unit, so...
			} else if (unit_index_to_save > -1) {
				// this rigamarole triggers save_learning_progression to only save this unit
				let u = lp_data.units[unit_index_to_save]
				u.unit_order = unit_index_to_save
				lp_data.units = [u]
			}

			// if we're saving a new course-type collection, we need to set the year to the currently-selected year, if this instance does year-by-year lps
			if (state.site_config.limit_courses_to_academic_year == 'yes' && lp_data.collection_type == 'course' && lp_data.lp_id == 0) {
				lp_data.year = state.user_info.academic_year
			}

			// global database edit lock keys on last checkout timestamp, from server, to detect users editing a collection in different tabs/windows
			// - edit_access_control gets lock when user opens for editing
			// - on save, save_learning_progression call save service on back end to ensure lock still valid or available
			// - edit control service also checks locks user_id and expiry timestamp
			// should not see edit lock on new lp
			let last_checkout_timestamp = state.lp_edit_locked[lp_data.lp_id+'']
			if (lp_data.lp_id == 0) {
				last_checkout_timestamp = 'initial_save'
			} else if (bypass_checkout) {
				last_checkout_timestamp = 'bypass_checkout'
			} else if (typeof(last_checkout_timestamp) == 'undefined') {
				// something went wrong
				console.log("save fail: no edit lock checkout timestamp in save_learning_progression")
				vapp.collection_edit_lock_conflict_msg('Saving collection failed')
				return
			}
			
			let payload = {
				user_id: state.user_info.user_id,
				lp_checkout_timestamp: last_checkout_timestamp,
				// encode blocked keywords in the lp_data to avoid firewall issues; setting encode_blocked_keywords tells ajax to decode on the server
				lp_data: U.encode_blocked_keywords(JSON.stringify(lp_data)),
				encode_blocked_keywords: ['lp_data'],
			}

			return new Promise((resolve, reject)=>{
				if (show_loader) U.loading_start()
				U.ajax('save_learning_progression', payload, result=>{
					if (show_loader) U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in store.save_learning_progression')
						vapp.ping()		// call ping to check if the session is expired

						vapp.collection_edit_lock_conflict_msg(result, lp.lp_id, '1')
						
						reject()
						return
					}

					// update the updated_at value of the lp, as well as lp_id and course_code, which might have been created
					lp.updated_at = result.lp_updated_at
					lp.lp_id = result.lp_id
					lp.course_code = result.course_code

					// add the lp to the user's admin_rights if it isn't already there
					let rights_string = `lp.course.${lp.course_code}`
					if (!state.user_info.admin_rights.includes(rights_string)) {
						commit('add_to_array', [state.user_info.admin_rights, rights_string])
						U.ajax('update_admin_rights', {user_id: state.user_info.user_id, admin_rights: state.user_info.admin_rights }, result =>{
							if (result.status != 'ok') {
								vapp.$alert('A problem occurred while attempting to save the collection user rights')
							}
						});
					}

					// set returned lp_unit_ids and lp_ids, in case one or more units is new (or was a "999999X" value)
					// but not if we didn't save any units
					if (unit_index_to_save != -2) {
						// if we only saved one unit, only update that unit
						if (unit_index_to_save > -1) {
							lp.units[unit_index_to_save].lp_unit_id = result.lp_unit_ids[0] * 1
							// the lp_id must have existed already if unit_index_to_save is set

						} else {
							for (let i = 0; i < lp.units.length; ++i) {
								lp.units[i].lp_unit_id = result.lp_unit_ids[i] * 1
								lp.units[i].lp_id = lp.lp_id * 1
							}
						}
					}

					// save updated lp_checkout_timestamp, in case this gets called again before the user "checks in" the LP
					commit('set', [state.lp_edit_locked, lp.lp_id+'', result.lp_checkout_timestamp])

					// if we moved resource out of a shared collection, e.g. Unpublished Content, clear edit lock when done
					if (bypass_checkout === true) {
						dispatch('edit_access_control_checkin', {lp_id: lp.lp_id})
					}
					
					// trigger updates
					commit('trigger_course_update')
					resolve(result)
				});
			})
		},

		archive_learning_progression({state, commit, displatch, getters}, lp) {
			let payload = {
				user_id: state.user_info.user_id,
				course_code: lp.course_code
			}
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('archive_learning_progression', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error archiving learning progression')
						vapp.ping() // call ping to check if the session is expired
						if (result.status != 'ok') {
							console.log('Error archiving learning progression')
							vapp.ping()		// call ping to check if the session is expired

							vapp.collection_edit_lock_conflict_msg(result, lp.lp_id, '2')

							reject()
							return
						}
					}
					// remove learning progression from state
					let index = state.all_courses.findIndex(o => o.course_code == lp.course_code)
					state.all_courses.splice(index, 1)
					resolve(result)
				})
			})
		},

		delete_learning_progression({state, commit, dispatch, getters}, lp) {
			let payload = {
				user_id: state.user_info.user_id,
				course_code: lp.course_code,
			}
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('delete_learning_progression', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error deleting curriculum map')
						vapp.ping()		// call ping to check if the session is expired
						
						vapp.collection_edit_lock_conflict_msg(result, lp.lp_id, '3')

						reject()
						return
					}
					// splice out of all_courses array
					let index = state.all_courses.findIndex(o=>o.course_code == lp.course_code)
					state.all_courses.splice(index, 1)
					resolve(result)
				});
			})
		},

		duplicate_learning_progressions({state, commit, dispatch}, course_codes) {
			let payload = {
				user_id: state.user_info.user_id,
				course_codes: course_codes,
			}
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('duplicate_learning_progression', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error duplicating curriculum map')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// reload all courses to get duplicates
					dispatch('get_all_courses')

					resolve()
				});
			})
		},

		copy_collection_to_subdomain({state, dispatch}, payload) {
			let ajax_payload = {
				user_id: state.user_info.user_id,
				source_course_code: payload.source_course_code,
				agency_id: payload.agency_id,
				copy_items: payload.copy_items,
			}
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('copy_collection_to_subdomain', ajax_payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						console.error('Error copying collection to subdomain')
						vapp.ping()
						reject()
						return
					}
					dispatch('get_all_courses')
					resolve(result)
				})
			})
		},

		share_learning_progression({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('share_learning_progression', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert(result.status); vapp.ping(); reject(); return;
					}
					resolve(result)
				})
			})
		},

		remove_user_from_shared_collection({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('remove_user_from_shared_collection', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					resolve()
				})
			})
		},

		admin_change_collection_type({state, commit, displatch}, payload) {
			payload.user_id = state.user_info.user_id;
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('admin_change_collection_type', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					const index = state.all_courses.findIndex(o => o.course_code == payload.course_code)
					if (index !== -1 && state.all_courses[index].collection_type !== payload.updated_collection_type) {
						commit('update_collection_type', { index, lp: result.lp });
					}
					resolve()
				})
			})
		},

		edit_user_shared_collection_rights({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('edit_user_shared_collection_rights', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					resolve()
				})
			})
		},

		generate_subscription_code({state, commit, dispatch}, lp_id) {
			const payload = {
				user_id: state.user_info.user_id,
				lp_id: lp_id
			}
			return new Promise((resolve, reject) => {
				U.ajax('generate_subscription_code', payload, result => {
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					// add subscription_code to the store record for the lp
					let collection = state.all_courses.find(x=>x.lp_id == lp_id)
					if (collection) {
						collection.subscription_code = result.subscription_code
						collection.updated_at = result.updated_at
					}
					resolve(result)
				})
			})
		},
		subscribe_to_collection({state, commit, dispatch}, sub_code) {
			const payload = {
				user_id: state.user_info.user_id,
				sub_code: sub_code
			}
			return new Promise((resolve, reject) => {
				// note that this service is poorly named; it doesn't actually create a subscription; it just returns the collection data for a subscription code
				U.ajax('subscribe_to_collection', payload, result => {
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					resolve(result.lp)
				})
			})
		},
		unsubscribe_from_shared_collection({state, commit, dispatch}, course_code) {
			const payload = {
				user_id: state.user_info.user_id,
				course_code: course_code
			}
			return new Promise((resolve, reject) => {
				U.ajax('unsubscribe_from_shared_collection', payload, result => {
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}
					// remove learning progression from state
					let index = state.all_courses.findIndex(o => o.course_code == course_code)
					state.all_courses.splice(index, 1)
					resolve()
				})
			})
		},

		// save the user's "default collection" items -- meaning the resources and lessons in the user's dummy default collection units
		save_default_collection_resources({state, commit, dispatch, getters}) {
			let payload = { user_id: state.user_info.user_id, resource_ids: [], lesson_ids: [] }
			let default_collection_id = getters.my_default_collection.default_collection_id_for_collection_asset_mapping()
			for (let unit of getters.my_default_collection.units) {
				// skip the placeholder resource if added
				for (let resource of unit.resources) if (!resource.placeholder) {
					// don't send resources that are mapped onto other collections
					if (state.my_ca_mappings.findIndex(x=>x.asset_id==resource.resource_id && x.asset_type=='resource' && x.collection_id!=default_collection_id) == -1) {
						payload.resource_ids.push(resource.resource_id)
						// add resource to my_resources if not already there
						if (!state.my_resources.find(x=>x.resource_id == resource.resource_id)) commit('set', [state.my_resources, 'PUSH', resource])
					}
				}
				for (let lesson of unit.lessons) {
					// don't send lessons that are mapped onto other collections
					if (state.my_ca_mappings.findIndex(x=>x.asset_id==lesson.lesson_id && x.asset_type=='lesson' && x.collection_id!=default_collection_id) == -1) {
						payload.lesson_ids.push(lesson.lesson_id)
						// add lesson to my_lessons if not already there
						if (!state.my_lessons.find(x=>x.lesson_id == lesson.lesson_id)) commit('set', [state.my_lessons, 'PUSH', lesson])
					}
				}
			}

			console.log('save_default_collection_resources', payload)

			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('save_default_collection_resources', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert('Error in ajax call: ' + result.status); vapp.ping(); reject(); return;
					}

					// add added_ca_mapping_records to my_ca_mappings
					for (let ca_mapping of result.added_ca_mapping_records) {
						state.my_ca_mappings.push(ca_mapping)
					}

					// if we got any removed_ca_mapping_records, 
					for (let ca_mapping_id of result.removed_ca_mapping_records) {
						let ca_mapping_index = state.my_ca_mappings.findIndex(x=>x.ca_mapping_id == ca_mapping_id)
						if (ca_mapping_index > -1) {
							let cam = state.my_ca_mappings[ca_mapping_index]

							// remove from my_resources/my_lessons if necessary. check first to see if the item is mapped to another collection
							if (!state.my_ca_mappings.find(x => x!=cam && x.asset_id==cam.asset_id && x.asset_type==cam.asset_type)) {
								if (cam.asset_type == 'lesson') {
									let index = state.my_lessons.findIndex(x=>x.lesson_id == cam.asset_id)
									if (index > -1) {
										console.log('deleting', index, cam.asset_id)
										state.my_lessons.splice(index, 1)
									} else {
										console.log('not deleting', cam.asset_id)
									}
								} else {
									let index = state.my_resources.findIndex(x=>x.resource_id == cam.asset_id)
									if (index > -1) state.my_resources.splice(index, 1)
								}
							}

							// remove from my_ca_mappings
							state.my_ca_mappings.splice(ca_mapping_index, 1)
						}
					}

					// update default collection
					commit('generate_default_collection')

					resolve()
				})
			})
		},

		// save the user's items in a shadow unit
		synch_shadow_unit_assets({state, commit, dispatch, getters}, shadow_unit) {
			let payload = {
				user_id: state.user_info.user_id,
				lp_id: shadow_unit.shadows_lp_id,
				lp_unit_id: shadow_unit.shadows_lp_unit_id,
				resource_ids: [],
				lesson_ids: [],
			}
			for (let resource of shadow_unit.resources) {
				// skip placeholder resources if there
				if (resource.placeholder == true) {
					console.log('skipping placeholder')
					continue
				}
				payload.resource_ids.push(resource.resource_id)
				// add resource to my_resources if not already there
				if (!state.my_resources.find(x=>x.resource_id == resource.resource_id)) commit('set', [state.my_resources, 'PUSH', resource])
			}
			for (let lesson of shadow_unit.lessons) {
				payload.lesson_ids.push(lesson.lesson_id)
				// add lesson to my_lessons if not already there
				if (!state.my_lessons.find(x=>x.lesson_id == lesson.lesson_id)) commit('set', [state.my_lessons, 'PUSH', lesson])
			}
			console.log('shadow unit and payload', shadow_unit, payload)

			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('synch_shadow_unit_assets', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in store.synch_shadow_unit_assets')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// add added_ca_mapping_records to my_ca_mappings
					for (let ca_mapping of result.added_ca_mapping_records) {
						state.my_ca_mappings.push(ca_mapping)
					}

					// if we got any removed_ca_mapping_records, 
					for (let ca_mapping_id of result.removed_ca_mapping_records) {
						let ca_mapping_index = state.my_ca_mappings.findIndex(x=>x.ca_mapping_id == ca_mapping_id)
						if (ca_mapping_index > -1) {
							let cam = state.my_ca_mappings[ca_mapping_index]

							// remove from my_resources/my_lessons if necessary. check first to see if the item is mapped to another collection
							if (!state.my_ca_mappings.find(x => x!=cam && x.asset_id==cam.asset_id && x.asset_type==cam.asset_type)) {
								if (cam.asset_type == 'lesson') {
									let index = state.my_lessons.findIndex(x=>x.lesson_id == cam.asset_id)
									if (index > -1) {
										console.log('deleting', index, cam.asset_id)
										state.my_lessons.splice(index, 1)
									} else {
										console.log('not deleting', cam.asset_id)
									}
								} else {
									let index = state.my_resources.findIndex(x=>x.resource_id == cam.asset_id)
									if (index > -1) state.my_resources.splice(index, 1)
								}
							}

							// remove from my_ca_mappings
							state.my_ca_mappings.splice(ca_mapping_index, 1)
						}
					}

					// update default collection
					commit('generate_default_collection')

					// caller should update shadow unit data structure
					resolve(result)
				});
			})
		},

		// global database edit lock keys on last checkout timestamp, from server, to detect users editing a collection in different tabs/windows
		// - edit_access_control gets lock when user opens for editing
		// - on save, save_learning_progression call save service on back end to ensure lock still valid or available
		// - edit control service also checks locks user_id and expiry timestamp
		edit_access_control({state, commit, dispatch, getters}, payload) {
			payload.user_id = state.user_info.user_id

			let last_checkout_timestamp = state.lp_edit_locked[payload.lp_id+'']
			last_checkout_timestamp = (typeof(last_checkout_timestamp) == 'undefined') ? 'initial_checkout' : last_checkout_timestamp
			payload.lp_checkout_timestamp = last_checkout_timestamp
			return new Promise((resolve, reject)=>{
				U.ajax('edit_access_control', payload, result=>{

					if (result.status == 'ok') {
						// vapp.$store.dispatch('edit_access_control', {lp_id: 803, action: 'check'})
						if (payload.action == 'check') console.log(result.edit_lock_data)
						// update the last_checkout_timestamp each edit request
						commit('set', [state.lp_edit_locked, payload.lp_id+'', result.lp_checkout_timestamp])
						resolve(result)

					} else if (result.status == 'session_conflict') {

						// this user has lock in another tab or window
						vapp.$confirm({
							title: 'Please Confirm',
							text:'You may be editing this content collection in a different tab, a different window, or a different browser. Are you sure you want to edit here instead?',
							acceptText: "Edit Here"
						}).then(y => {
							// update edit_lock session_id and extend edit time
							payload.action = 'transfer_session'

							//payload.lp_checkout_timestamp = result.lp_checkout_timestamp
							// this updates session id in edit lock and checkout record, and informs user
							// if user then attempts to save, edit lock will be checked during save in new window
							U.ajax('edit_access_control', payload, transfer_result=>{
								// save the latest checkout time from updated db edit_lock
								commit('set', [state.lp_edit_locked, payload.lp_id+'', transfer_result.lp_checkout_timestamp])

								vapp.$inform('Collection “checked out” for editing.')
								resolve(transfer_result)
							})
						}).catch(n => {
							reject()
						}).finally(f=>{})
					} else {
						vapp.collection_edit_lock_conflict_msg(result, payload.lp_id, '4')
						reject()
					}
				});
			})
		},

		// check-in an LP from editing. payload should include only an lp_id; if it's 0, don't send this (in that case it was presumably a new lp
		edit_access_control_checkin({state, commit, dispatch}, payload) {
			// note that we don't do a promise here, because there will be no action based on the return value
			if (payload.lp_id == 0) {
				console.log('checkin request: lp_id is 0, so returning')
				return
			}

			payload.action = 'checkin'
			payload.user_id = state.user_info.user_id
			// checkin will clear edit lock in db
			payload.lp_checkout_timestamp = 'checking_in'
		
			U.ajax('edit_access_control', payload, (result)=>{
				if (result.status == 'ok') {
					console.log('checkin request: ' + result.status)
				} else {
					console.log('checkin request failed: ', result)
				}
			})
		},

		save_resource({state, commit, dispatch}, args) {
			let payload = args.resource.copy_for_save()

			payload.user_id = state.user_info.user_id

			// if (!empty(args.uploaded_file_data)) {
			// 	payload.uploaded_file_data = args.uploaded_file_data
			// }
			let override_options = null
			if (!empty(args.uploaded_file)) {
				// file upload
				if (payload.type == 'upload') {
					let fd = new FormData()
					fd.append('file', args.uploaded_file)
					for (let key in payload) {
						// for standards we have to stringify, because we can't append an object to the FormData (it would be converted to "[object Object],[object Object]")
						if (key == 'standards') fd.append(key, JSON.stringify(payload[key]))
						// we will never need lti_params for an uploaded file-type resource
						else if (key == 'lti_params') continue
						// FormData.append stringifies null/undefined into the literal "null"/"undefined"; skip instead
						else if (payload[key] === null || payload[key] === undefined) continue
						else fd.append(key, payload[key])
					}
					payload = fd
					override_options = {contentType: false, processData: false}
				} else {
					// html, entered in the resource editor directly
					payload.html = args.uploaded_file
				}
			}

			return new Promise((resolve, reject)=>{
				if (args.no_loader != true) U.loading_start()
				U.ajax('save_resource', payload, result=>{
					if (args.no_loader != true) U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error updating resource')
						vapp.ping()		// call ping to check if the session is expired
						reject(result.status)
						return
					}

					// resolve with the resource data
					resolve(result.resource)
				}, override_options);
			})
		},

		delete_resource({state, commit, dispatch}, resource) {
			let payload = {
				user_id: state.user_info.user_id,
				resource_id: resource.resource_id,
			}
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('delete_resource', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error deleting resource')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// caller is responsible for keeping track of the resource...
					resolve(resource.resource_id)
				});
			})
		},

		save_resource_collection({state, commit, dispatch}, payload) {
			// payload should contain 'resource_collection_title', 'resource_collection_json' and 'resources' (array)
			payload.user_id = state.user_info.user_id

			// stringify json and array
			payload.resource_collection_json = JSON.stringify(payload.resource_collection_json)
			payload.resources = JSON.stringify(payload.resources)

			return new Promise((resolve, reject)=>{
				U.loading_start('Saving Common Cartridge resources; this may take some time...')
				U.ajax('save_resource_collection', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error saving resource collection')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// resolve with resource_collection_resource_id
					resolve(result.resource_collection_resource_id)
				})
			})
		},

		save_resource_completion({state, commit, dispatch}, payload) {
			// payload should contain 'resource_id' and 'todo_status', which can be 0 (not complete), 100 (complete), or 5-95 (partially complete video); add user_id
			payload.user_id = state.user_info.user_id

			return new Promise((resolve, reject)=>{
				U.ajax('save_resource_completion', payload, result=>{
					if (result.status != 'ok') {
						console.log('Error saving resource completion')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// completion timestamp should come back from service if set; otherwise we'll get back the todo_status we sent in; either way, save in store
					commit('set', [state.user_info.todo_status, payload.resource_id, result.todo_status])

					// note that this may be called without a then()
					resolve()
				})
			})
		},

		save_todo_user_group({state, commit, dispatch}, payload) {
			// payload should contain 'todo_user_id' and 'todo_user_group' (a uuid); add the signed-in user's user_id
			payload.user_id = state.user_info.user_id

			// if todo_user_group is empty, send '*CLEAR*' to clear it out
			if (payload.todo_user_group == '') payload.todo_user_group = '*CLEAR*'

			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('save_todo_user_group', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in save_todo_user_group')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// if todo_user_id is the signed-in user, set todo_user_group for state.user_info
					if (payload.todo_user_id == state.user_info.user_id) {
						if (payload.todo_user_group == '*CLEAR*') {
							commit('set', [state.user_info, 'todo_user_group', ''])
						} else {
							commit('set', [state.user_info, 'todo_user_group', payload.todo_user_group])
						}
					}

					// note that this may be called without a then()
					resolve()
				})
			})
		},

		get_todo_report_data({state, commit, dispatch}, payload) {
			// payload should contain 'todo_user_group' (a uuid, or 'all' for all groups); add user_id
			payload.user_id = state.user_info.user_id

			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('get_todo_report_data', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in get_todo_report_data')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					commit('set', [state.todo_report_group_data, payload.todo_user_group, result.todo_report_data])

					// note that this may be called without a then()
					resolve()
				})
			})
		},

		get_all_courses({state, commit, dispatch}, flag) {
			if (!flag) flag = ''
			let payload = {
				user_id: state.user_info.user_id,
			}
			
			// add subdomain if we have one
			if (state.subdomain_data) {
				payload.subdomain = state.subdomain_data.subdomain
			}

			return new Promise((resolve, reject)=>{
				if (flag.includes('initial')) {
					if (!empty(state.all_courses_initial)) {
						if (state.all_courses_initial.complete) {
							resolve()
						} else {
							state.all_courses_initial.callbacks.push(resolve)
						}
						return
					} else {
						state.all_courses_initial = { complete: false, callbacks: [resolve] }
					}
				}

				if (!flag.includes('no_loader')) U.loading_start()
				U.ajax('get_all_courses', payload, result=>{
					if (!flag.includes('no_loader')) U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error retrieving all courses')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					// console.log('get_all_courses', result)

					for (let course of result.all_courses) {
						let lp = new Learning_Progression(course)
						// if we already have the course in all_courses...
						let index = state.all_courses.findIndex(o=>o.course_code == course.course_code)
						if (index > -1) {
							let existing_course = state.all_courses[index]
							// then only overwrite what we already have if it wasn't fully loaded
							if (!existing_course.fully_loaded) {
								state.all_courses[index] = lp
							} else {
								// otherwise fill in the value for cmap_specified, which may not have come in initially
								commit('set', [existing_course, 'cmap_specified', lp.cmap_specified])
							}
						} else {
							// else push onto all_courses
							commit('set', [state.all_courses, 'PUSH', lp])
						}
					}
					commit('set', ['all_courses_loaded', true])

					if (state.all_courses_initial && !state.all_courses_initial.complete) {
						state.all_courses_initial.complete = true
						for (let cb of state.all_courses_initial.callbacks) cb()

					} else {
						resolve()
					}
				});
			})
		},

		save_activity({state, commit, dispatch}, payload) {
			// payload must include:
			// activity_data, which should have been run through Activity.copy_for_save and will be stringified here
			// optional: assignees, which should have been run through Assignee.copy_for_save and will be stringified here if provided
			// optional: suppress_loader (boolean)
			// TODO: deal with activity_class ('teacher' or 'template')
			payload.activity_data = JSON.stringify(payload.activity_data)
			if (payload.assignees) payload.assignees = JSON.stringify(payload.assignees)
			payload.user_id = state.user_info.user_id
			return new Promise((resolve, reject)=>{
				let suppress_loader = payload.suppress_loader
				if (suppress_loader) delete payload.suppress_loader
				else U.loading_start(payload.loader_msg)	// loader_msg will be set if we're updating IC gradebook settings
				delete payload.loader_msg

				U.ajax('save_activity', payload, result=>{
					if (!suppress_loader) U.loading_stop()
					if (result.status != 'ok') {
						console.error('Error in save_activity call'); vapp.ping(); reject(result); return;
					}

					if (result.activity) {
						// add/update the "raw" ca_mapping data in my_ca_mappings
						let ca_mapping = Activity.convert_to_ca_mapping(result.activity)
						let ca_mapping_index = state.my_ca_mappings.findIndex(x=>x.ca_mapping_id == ca_mapping.ca_mapping_id)
						if (ca_mapping_index == -1) {
							state.my_ca_mappings.push(ca_mapping)
						} else {
							state.my_ca_mappings.splice(ca_mapping_index, 1, ca_mapping)
						}
					}

					// note that it's up to the caller to save changes to the activity to the store
					resolve(result)
				});
			})
		},

		save_lesson({state, commit, dispatch}, payload) {
			// payload must include:
			// lesson_class ('master', 'teacher' or 'template')
			// lesson_data, which should have been run through Activity.copy_for_save and will be stringified here
			// optional: suppress_loader (boolean)
			payload.lesson_data = JSON.stringify(payload.lesson_data)
			payload.user_id = state.user_info.user_id
			return new Promise((resolve, reject)=>{
				let suppress_loader = payload.suppress_loader
				if (suppress_loader) delete payload.suppress_loader
				else U.loading_start()

				U.ajax('save_lesson', payload, result=>{
					if (!suppress_loader) U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in save_lesson call'); vapp.ping(); reject(); return;
					}

					// note that it's up to the caller to save changes to store
					resolve(result)
				});
			})
		},

		save_my_activity_result({state, commit, dispatch}, activity_id) {
			return new Promise((resolve, reject)=>{
				let ar = state.my_activity_results[activity_id]
				if (empty(ar)) {	// shouldn't happen
					console.log('no_my_activity_result')
					reject()
					return
				}

				let payload = {
					user_id: state.user_info.user_id,
					activity_result_data: JSON.stringify(ar.copy_for_save())
				}

				// if this student's results need to go into the IC gradebook, add relevant params
				let activity = state.my_activities.find(x=>x.activity_id == activity_id)
				if (activity) activity.add_gradebook_data_to_payload(payload)
		
				// U.loading_start()
				U.ajax('save_activity_result_by_student', payload, result=>{
					// U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in save_activity_result_by_student call'); vapp.ping(); reject(); return;
					}

					// replace result record in my_activity_results
					commit('set', [state.my_activity_results, activity_id+'', new Activity_Result(result.activity_result)])
					resolve()
				});
			})
		},
	
		get_messages({state, commit, dispatch}, payload) {
			// don't try to get messages if we don't have a sis_user_sourcedId (unless we happen to be viewing as parent)
			if (state.user_info.sis_user_sourcedId == '' && state.user_info.role !== 'parent') return

			payload.user_id = state.user_info.user_id
			payload.sis_user_sourcedId = state.user_info.sis_user_sourcedId
			payload.role = state.user_info.role

			// add a timestamp of when the last message load happened so we can only load new messages; if this value is 0 (the initial value in state), all messages will be retrieved
			payload.message_load_timestamp = state.message_load_timestamp
			return new Promise((resolve, reject)=>{
				// only show loading indicator for the first time we're doing this
				if (payload.message_load_timestamp == 0) U.loading_start()
				U.ajax('get_messages', payload, result=>{
					if (payload.message_load_timestamp == 0) U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error retrieving messages for course')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}

					let filtered_messages = result?.messages ?? []
					if (state.user_info.role === 'student') {
						filtered_messages = filtered_messages?.filter(message => message.send_to !== 'Guardian')
					}
					if (state.user_info.role === 'parent') {
						filtered_messages = filtered_messages?.filter(message => message.send_to !== 'Student')
					}

					// store incoming data...
					for (let message_data of filtered_messages) {
						// console.log(`message_data ${JSON.stringify(message_data)}`)
						let message = new window.Message(message_data)
						commit('replace_in_array', [state.messages, 'message_id', message.message_id, message])
					}

					// store incoming message_load_timestamp
					state.message_load_timestamp = result.message_load_timestamp
				})
			})
		},

		save_message({ state, commit, dispatch }, message) {
			console.log('saving message', message)
			let payload = {
				user_id: state.user_info.user_id,
				message_id: message.message_id,
				author_user_id: state.user_info.user_id,
				course_code: message.course_code,
				parent_message_id: message.parent_message_id,
				message_level: message.message_level,
				recipients: JSON.stringify(message.recipients),
				subject: message.subject,
				send_to: message.send_to,
				send_at: message.send_at,
				first_name: state.user_info.first_name,
				last_name: state.user_info.last_name,
				author_sourcedId: state.user_info.sis_user_sourcedId,
				activity_id: message.activity_id,
			}
			if (!empty(message.body)) payload.body = message.body
			return new Promise((resolve, reject) => {
				U.loading_start()
				console.log('promising')
				U.ajax('save_message', payload, result => {
					console.log('promising done')
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping() // call ping to check if the session is expired
						console.log('Error saving message')
						reject()
						return
					}
					// update message_id, in case this was a new message being saved
					message.message_id = result.message_id
					message.created_at = date.parse(result.created_at, 'YYYY-MM-DD HH:mm:ss').getTime() / 1000
					message.is_read = 1 // author has read the message by default
					resolve()
				})
			})
		},

		delete_message({state, commit, dispatch}, message) {
			return new Promise((resolve, reject)=>{
				let index = state.messages.findIndex(o=>o == message)
				state.messages.splice(index, 1)
				resolve()
				return
			})
		},

		archive_message({state, commit, dispatch}, message) {
			return new Promise((resolve, reject)=> {
				U.ajax('archive_message', {user_id: state.user_info.user_id, message_id: message.message_id}, result=>{
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						console.log('Error archiving message')
						reject()
						return
					}
					let index = state.messages.findIndex(o=>o.message_id == message.message_id)
					state.messages.splice(index, 1)
					resolve()
				});
			})
		},

		mark_message_as_read({state, commit, dispatch}, message) {
			const payload = {
				user_id: state.user_info.user_id,
				user_sourcedId: state.user_info.sis_user_sourcedId,
				message_id: message.message_id,
			}
			let index = state.messages.findIndex(o=>o.message_id == message.message_id)
			state.messages[index].is_read = 1
			return new Promise((resolve, reject)=>{
				U.ajax('mark_message_as_read', payload, result=>{
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						console.log('Error marking message as read')
						reject()
						return
					}
					resolve()
				})
			})
		},

		save_message_preferences({state, commit, dispatch}, prefs) {
			// console.log('saving message prefs', JSON.stringify(prefs))
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('save_message_preferences', prefs, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						console.log('Error saving message')
						reject()
						return
					}
					commit('set', [state.user_info, 'message_preferences', result.updated_prefs])
					resolve()
				});
			})
		},

		lti_launch({state, commit, dispatch}, payload) {
			// To do an LTI launch, we call a service to get the LTI form html along with the javascript to auto-submit the form,
			// then we open a new window and write the html/js
			payload.user_id = state.user_info.user_id
			U.loading_start()
			U.ajax('get_lti_1_launch_form', payload, result=>{
				U.loading_stop()
				if (result.status != 'ok') {
					vapp.ping()		// call ping to check if the session is expired
					vapp.$alert('An error occurred when attempting to launch the resource.')
					return
				}

				// see https://developer.mozilla.org/en-US/docs/Web/API/Window/open
				let w = window.open()
				w.document.write(result.lti_form)
			});
		},

		get_resource_record({state, commit, dispatch}, payload) {
			// payload should include the resource_id being queried; this fn is mainly (or possibly exclusively) used for lti launches
			// if payload includes "get_lti_form:'yes'", we will retrieve the form to do the launch

			// we have to send the user's *email address* for this service
			payload.email = state.user_info.email

			// don't show loading indicator here, as we call the service on hover for resources
			// U.loading_start()
			return new Promise((resolve, reject)=>{
				U.ajax('get_resource_record', payload, result=>{
					// U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to retrieve data about the resource.')
						reject()
						return
					}

					resolve(result)
				});
			})
		},

		/** log to asset_log table */
		save_asset_log({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject)=>{
				U.ajax('save_asset_log', payload, result => {
					resolve(result)
				});
			})
		},

		/** save asset_review_payment */
		save_asset_review_payment({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				U.ajax('save_asset_review_payment', payload, result => {
					resolve(result)
				});
			})
		},

		// log to resource_usage table
		log_resource_usage({state, commit, dispatch}, payload) {
			// state.lp_showing is set in App.vue on the basis of the route
			// PW 8/30/2026: this isn't always accurate, but we now try to look up the course_code on the server from the collection_asset_mapping table
			payload.course_code = state.lp_showing
			return new Promise((resolve, reject)=>{
				U.ajax('log_resource_usage', payload, result=>{
					// don't block other activity for logging errors
					// return result; see SparklBankStandaloneView
					resolve(result)
				});
			})
		},

		get_lsdoc_list({state, commit, dispatch}) {
			let payload = { user_id: state.user_info.user_id }

			// add subject-area framework identifiers to payload, since we only need to retrieve data for them
			let arr = []
			for (let s in state.subjects) {
				arr.push(state.subjects[s].framework_identifier)
				// allow for alt_frameworks
				let alt_frameworks = state.subjects[s].alt_frameworks
				if (alt_frameworks) for (let a in alt_frameworks) {
					arr.push(alt_frameworks[a])
				}
			}
			payload.framework_identifier_filter = JSON.stringify(arr)

			// set frameworks_loading to true BEFORE framework_records have been loaded, so that other courses' pages won't also load the framework list
			state.frameworks_loading = true

			U.loading_start()
			return new Promise((resolve, reject)=>{
				U.ajax('get_framework_list', payload, result=>{
					U.loading_stop()

					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to retrieve the standards frameworks list.')
						reject()
						return
					}

					let arr = []
					for (let record of result.records) {
						// console.log(record.framework_identifier)
						let json = JSON.parse(record.json)
						let doc = new CFDocument(json.case_document_json)

						// for now, skip records that don't match frameworks in our subjects array
						let found = false
						for (let s in state.subjects) {
							if (state.subjects[s].framework_identifier == doc.identifier) {
								found = true
								break
							}
							// allow for alt_frameworks
							let alt_frameworks = state.subjects[s].alt_frameworks
							if (alt_frameworks) for (let a in alt_frameworks) {
								if (alt_frameworks[a] == doc.identifier) {
									found = true
									break
								}
							}
						}

						if (!found) continue

						delete(json.case_document_json)
						let framework_record = U.create_framework_record(doc.identifier, {CFDocument: doc}, json, false)
						arr.push(framework_record)
					}

					// sort by title, keeping fine arts and CTAE at the end
					arr = arr.sort((a,b) => {
						if (a.json.CFDocument.title.indexOf('CTAE') > -1 && b.json.CFDocument.title.indexOf('CTAE') == -1) return 1
						if (b.json.CFDocument.title.indexOf('CTAE') > -1 && a.json.CFDocument.title.indexOf('CTAE') == -1) return -1

						if (a.json.CFDocument.title.indexOf('Fine Arts') > -1 && b.json.CFDocument.title.indexOf('Fine Arts') == -1) return 1
						if (b.json.CFDocument.title.indexOf('Fine Arts') > -1 && a.json.CFDocument.title.indexOf('Fine Arts') == -1) return -1

						if (a.json.CFDocument.title.toLowerCase() < b.json.CFDocument.title.toLowerCase()) return -1
						if (b.json.CFDocument.title.toLowerCase() < a.json.CFDocument.title.toLowerCase()) return 1
						return 0
					})

					state.framework_records = arr

					// now set frameworks_loading to false and frameworks_loaded to true
					state.frameworks_loading = false
					state.frameworks_loaded = true

					resolve()
				})
			})
		},

		get_lsdoc({state, commit, dispatch}, lsdoc_identifier) {
			return new Promise((resolve, reject)=>{
				// if lsdoc_identifier is an object, extract include_supplemental_branches (see U.framework_record_build_tree)
				let include_supplemental_branches = false
				if (typeof(lsdoc_identifier) == 'object') {
					include_supplemental_branches = lsdoc_identifier.include_supplemental_branches
					lsdoc_identifier = lsdoc_identifier.lsdoc_identifier
				}

				// note that this gets the 'vext' version of the file
				let filepath = sr('frameworks/$1.json', lsdoc_identifier)
				// TODO: get_json_file will throw an error if the json is malformed; need to test what happens if that happens.

				// if framework_record doesn't already exist, create it
				let fr = U.retrieve_or_create_framework_record(lsdoc_identifier)

				// set fr.framework_json_loaded to 'loading'; this allows callers to know if we're already in the process of retrieving the framework
				fr.framework_json_loaded = 'loading'

				U.get_json_file(filepath, result=>{
					if (typeof(result) == 'string') {
						console.log('Error in get_lsdoc', result)
						reject(result)
						return
					}

					// framework_record must exist (we created it above if it didn't already), so all we have to do is store the json and set framework_json_loaded to true, which framework_record_load_json does
					U.framework_record_load_json(fr, result)
					U.framework_record_build_tree(fr, include_supplemental_branches)

					resolve()
					// note that we don't need to load the exemplar frameworks in Inspire
				})
			})
		},

		get_framework_asset_counts({state, commit, dispatch}, framework_identifier) {
			return new Promise((resolve, reject)=>{
				// if framework_asset_counts is already filled in for the framework_identifier, send the value back
				let fac = state.framework_asset_counts[framework_identifier]
				if (!empty(fac)) {
					resolve(fac)
					return
				} else if (fac == 'loading') {
					resolve({})
					return
				}

				// set value for the framework to 'loading' so that we don't try to load multiple times
				commit('set', [state.framework_asset_counts, framework_identifier, 'loading'])

				let payload = {
					framework_identifier: framework_identifier
				}
				U.ajax('get_asset_counts_for_framework', payload, result=>{
					let o = {}
					if (result.status == 'ok' && !empty(result.counts)) {
						// we get back an array of objects with cfitem_identifier and ct; format into a hash by identifier
						for (let i of result.counts) {
							o[i.cfi_id] = i.ct
						}
					} else {
						console.warn('error in get_framework_asset_counts', result)
					}
					// note that we may not have received any rows; that is interpreted as there are no assets for any of the cfitems in the framework

					commit('set', [state.framework_asset_counts, framework_identifier, o])
					
					// resolve with the loaded counts
					resolve(state.framework_asset_counts[framework_identifier])
				})
			})
		},

		get_lesson_masters({state, commit, dispatch}, force) {
			return new Promise((resolve, reject)=>{
				// if support_og_lessons is 'no' (default is 'yes'), we don't need external lesson masters for this cureum instance
				if (state.site_config.support_og_lessons == 'no') {
					resolve()
					return
				}

				// unless force is true, only load masters if we haven't already done so
				if (state.loading_lesson_masters || (force !== true && state.lesson_masters.length > 0)) {
					resolve()
					return
				}

				state.loading_lesson_masters = true

				U.loading_start()
				U.ajax('get_lesson_masters', {user_id: state.user_info.user_id}, result=>{
					U.loading_stop()
					state.loading_lesson_masters = false
					state.lesson_masters = []

					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to retrieve the lesson masters (' + result.status + ').')
						reject()
						return
					}

					for (let lm of result.masters) {
						// legacy: masters originally had lc_content fields for each component; convert to lc_default_content
						for (let lc of lm.lesson_plan) if (lc.lc_content&&typeof(lc.lc_content)=='string') { lc.lc_default_content = lc.lc_content }
						state.lesson_masters.push(new Lesson(lm))
					}

					// sort so that newer masters are earlier in the list
					state.lesson_masters.sort((a,b)=>b.lesson_id-a.lesson_id)

					if (result.default_lesson_master_id != 0) {
						state.default_lesson_master = state.lesson_masters.find(x=>x.lesson_id == result.default_lesson_master_id)
						if (empty(state.default_lesson_master)) {
							// this shouldn't happen
							vapp.$alert('Couldn’t identify default lesson master (' + result.default_lesson_master_id + ')')
						}
					}

					resolve()
				});
			})
		},

		load_lesson({state, commit, dispatch}, lesson) {
			// fully-load a lesson that previously just had its lesson_id and lesson_title loaded
			U.loading_start()
			return new Promise((resolve, reject)=>{
				U.ajax('get_lesson', {user_id: state.user_info.user_id, lesson_id: lesson.lesson_id, lp_flag: 'no_load'}, result=>{
					U.loading_stop()
					if (result.status == 'not_found') {
						vapp.$alert('The specified lesson (L' + lesson.lesson_id + ') does not exist.')
						reject()
						return
					} else if (result.status != 'ok') {
						console.log('Error in ajax call'); vapp.ping(); reject(); return;
					}

					let temp_lesson = new Lesson(result.lesson)
					for (let prop in temp_lesson) {
						commit('set', [lesson, prop, temp_lesson[prop]])
					}

					resolve(lesson)
				})
			})
		},

		// server side pdf writer if adding resource pdfs to lesson
		save_to_pdf({state, commit, dispatch}, payload) {
			payload.user_id = state.user_info.user_id

			U.loading_start()
			return new Promise((resolve, reject)=>{
				U.ajax('save_to_pdf', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to save this page as pdf.')
						reject()
						return
					}
					resolve(result)
				});
			})
		},

		get_home_page_content({state, commit, dispatch}, home_page_variant) {
			return new Promise((resolve, reject)=>{
				// if we already have home_page_content, immediately resolve (and return)
				if (home_page_variant in state.home_page_content) {
					resolve()
					return
				}
				U.ajax('get_home_content', {home_page_variant: home_page_variant}, result=>{
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to retrieve the home page content.')
						return
					}
					commit('set', [state.home_page_content, home_page_variant, result.home_page_content])
					resolve()
				})
			})
		},

		get_user_records({state, commit}, payload) {
			// get user records from the DB
			// payload can include a `user_ids` array or an `emails` array
			payload.user_id = state.user_info.user_id
			return new Promise((resolve, reject)=>{
				U.ajax('get_user_records', payload, result=>{
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						vapp.$alert('An error occurred when attempting to retrieve user records.')
						return
					}
					let arr = []
					for (let user_data of result.users) {
						let u = new User_Info(user_data)
						u.email = u.email.toLowerCase() // make sure all emails are lower case
						arr.push(u)
						let i = state.user_records.findIndex(x=>x.user_id==user_data.user_id)
						if (i == -1) state.user_records.push(u)
						else state.user_records.splice(i, 1, u)
					}
					resolve(arr)
				})
			})
		},

		admin_get_term_settings({state, commit, dispatch}) {
			const payload = {
				user_id: state.user_info.user_id
			}
			return new Promise((resolve, reject)=>{
				U.ajax('admin_get_term_settings', payload, result=>{
					if (result.status != 'ok') {
						vapp.$alert('An error occurred when attempting to get the term settings.')
						reject()
						return
					}
					if (state.user_info.role == 'admin') {
						commit('set', ['term_settings', result.term_settings])
					}
				})
				resolve()
			})
		},

		admin_update_term_settings({state, commit, dispatch}, payload) {
			payload.user_id = state.user_info.user_id
			return new Promise((resolve, reject)=>{
				U.ajax('admin_update_term_settings', payload, result=>{
					if (result.status != 'ok') {
						vapp.$alert('An error occurred when attempting to update the term settings.')
						reject()
						return
					}
				})
				resolve()
			})
		},

		check_out_lesson_for_editing({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				return U.ajax('check_out_lesson_for_editing', payload, res => {
					if(res.status === 'lock_conflict') {
						vapp.$alert(`This lesson is currently checked out for editing by ${res.email}. Please try again later (the edit lock will expire in 15 minutes or less).`)
						reject('checkout rejected due to edit conflict')
					} else resolve(res)
				})
			})
		},

		// this.$store.dispatch('update_sis_data')
		update_sis_data({state, commit, dispatch}) {
			let payload = {
				user_id: state.user_info.user_id,
			}

			U.ajax('clear_sis_data', payload, result=>{
				// this will clear the user's sis data and prepare things so that when we reload, initialize_app will refresh the sis data
				document.location.reload()
			});
		},

		save_coteacher({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('save_coteacher', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping()		// call ping to check if the session is expired
						console.log('Error saving coteacher')
						reject()
						return
					}
					let ct = new window.Coteacher(result.coteacher)
					if (state.my_coteachers.findIndex(o=>o.coteacher_id == ct.coteacher_id) == -1) {
						// we are adding a new coteacher
						commit('set', [state.my_coteachers, 'PUSH', ct])
					} else {
						// we are edting an existing coteacher
						commit('replace_in_array', [state.my_coteachers, 'coteacher_id', ct.coteacher_id, ct])
					}
					resolve()
				})
			})
		},

		delete_coteacher({state, commit, dispatch}, coteacher) {
			let payload = {
				user_id: state.user_info.user_id,
				coteacher_id: coteacher.coteacher_id,
			}
			return new Promise((resolve, reject)=>{
				U.loading_start()
				U.ajax('delete_coteacher', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.$alert('An error occurred when attempting to delete the coteacher.')
						vapp.ping()		// call ping to check if the session is expired
						reject()
						return
					}
					// splice out of coteachers array
					let index = state.my_coteachers.findIndex(o=>o == coteacher)
					state.my_coteachers.splice(index, 1)
					resolve()
				})
			})
		},

		// used in UserRoleSelectionInterface
		load_school_districts({state, commit, dispatch}, reload) {
			return new Promise((resolve, reject)=>{
				// if reload isn't true and we already have school_districts, return
				if (state.school_districts.length > 0 && reload != true) {
					resolve()
					return
				}

				// otherwise retrive and parse the file containing districts
				U.loading_start()
				$.ajax({
					url: '/src/config/school_districts.tsv',
					dataType: 'text',
					success: (file_content) => {
						console.log('school district file retrieved successfully')
						// add a line for CGLT
						file_content += `\nCOMMON GOOD LEARNING TOOLS	OR	1	Multnomah County	0	0`
						let lines = file_content.split('\n')
						let arr = []

						// skip header by starting loop at index 1
						for (let i = 1; i < lines.length; i++) {
							let line = lines[i].trim()
							
							if (!line) continue

							let columns = line.split('\t')
							
							let o = {
								agency_name: columns[0],
								state: columns[1],
								agency_id: columns[2],
								county: columns[3],
								student_count: parseInt(columns[4]) || 0,
								teacher_count: parseInt(columns[5]) || 0
							}

							arr.push(o)
						}
						state.school_districts = arr
						U.loading_stop()
						resolve()
					},
					error: (jq_xhr, text_status, error_thrown) => {
						console.error('failed to retrieve school_districts file:', text_status)
						U.loading_stop()
						reject()
					}
				})
			})
		},

		// this.$store.dispatch('sign_out')
		sign_out({state, commit, dispatch}) {
			// clear the session_id from localstorage, since it's no longer valid
			U.local_storage_clear('gaconnects_session_id')

			// also clear simulated_user and simulated_role from lst
			commit('lst_set', ['simulated_user', ''])
			commit('lst_set', ['simulated_role', ''])

			U.loading_start()
			// if we're using GA OIDC, signout of the oidc system first
			if (state.site_config.login_method == 'oidc') {
				let sw = window.open('https://gaconnects.gadoe.org/Account/signout')
				setTimeout(x => {
					// close the signout window
					sw.close()

					U.ajax('sign_out', { user_id: state.user_info.user_id }, result => {
						// regardless of result, reload the page, so we're in non-signed-in mode
						document.location = '/home'
					});
				}, 10)
			} else {
				U.ajax('sign_out', { user_id: state.user_info.user_id }, result => {
					// regardless of result, reload the page, so we're in non-signed-in mode
					
					let new_path
					// if sign_in is required, go to the /signin route
					if (state.site_config.require_sign_in == 'yes') {
						new_path = '/signin'

					// else go to the /home route
					} else {
						new_path = '/home'
					}

					document.location = new_path
				});
			}
		},

		// this.$store.dispatch('move_units')
		move_units({state, commit, dispatch}, payload) {
			return new Promise((resolve, reject) => {
				U.loading_start()
				U.ajax('move_units', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') {
						vapp.ping()
						console.error('Error moving units')
						reject()
					} else {
						resolve(result)
					}
				})
			})
		},
	}
})
